/* eslint-disable max-len, max-lines, max-lines-per-function, id-length */
import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ADD_PROJECT_TITLE, SAVE, SELECT } from '../../../Constants/strings';
import { MODULE_NAMES } from '../../../Constants/modules';
import './addProject.scss';
import ButtonComponent from '../../../Components/Button/button';
import SearchDropdown from '../../../Components/Dropdown/dropdown';
import Loader from '../../../Components/Loader';
import {
  FormControl,
  Autocomplete,
  TextField,
  Select as MuiSelect,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import dayjs from 'dayjs';
import UseAxios from '../../../Api/http/http';
import {
  fetchAddProject,
  fetchAddProjectPrefill,
  updateAddProject,
  fetchProjectTechnologyList,
  createProjectTechnology,
  validateDlGroupId,
  validateJiraProjectId,
} from '../../../Api/request/addProject';
import { getProjectStatusMaster } from '../../../Api/request/projectDetails';
import { useAuth } from '../../../Contexts/AuthContext';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import Calendaricon from '../../../Assets/images/svg/dar_calendar_blank_gray.svg';
import dropdwn from '../../../Assets/images/svg/dar_dropdown_arrow.svg';
import backIcon from '../../../Assets/images/svg/black_back_button.svg';
import CommonDatePicker from '../../../Components/Datepicker/datepicker';

// ==================== API IMPORTS ====================
import {
  salesVerticalData,
  opportunityData,
  deliveryMethodologyData,
  revenueSplitData,
  customerTypeData,
  consumptionTypeData,
  technologyData,
  projectOfferingData,
  getRegionsData,
  regionCountryData,
  getSponsorsData,
  getSponsorPartnersData,
  getProjectDetailsLegacyVerticalData,
  seniorManagerData,
  clientDetailsData,
  cloudProviderData,
  costSplitData,
  fpBillingTypeData,
  tmBillingTypeData,
  billingEntityData,
  masterProjectNameData,
} from '../../../Api/request/projectOverviewMasterData';
import { getTravelDestination } from '../../../Api/request/costMaster';
import { zohoCustomerDetailsList } from '../../../Api/request/zohoCustomerDetails';

// ==================== CONSTANTS ====================

const PROJECT_TYPES = {
  MASTER: 'master',
  TIME_AND_MATERIAL: 'TM',
  FIXED_PRICE: 'FP',
};

const PROJECT_TYPE_OPTIONS = [
  { id: 1, name: 'Add new Master project', value: 'Add new Master project', type: PROJECT_TYPES.MASTER },
  { id: 2, name: 'Add new TM project', value: 'Add new TM project', type: PROJECT_TYPES.TIME_AND_MATERIAL },
  { id: 3, name: 'Add new FP project', value: 'Add new FP project', type: PROJECT_TYPES.FIXED_PRICE },
];

const COMMON = [PROJECT_TYPES.TIME_AND_MATERIAL, PROJECT_TYPES.FIXED_PRICE];

const MANAGER_FIELDS = new Set([
  'deliveryManager',
  'seniorManager',
  'pm1',
  'pm2',
  'pm3',
  'pm4',
  'pm5',
  'pm6',
  'pm7',
  'pm8',
  'pm9',
  'pm10',
  'pm11',
  'pm12',
  'pm13',
  'pm14',
  'pm15',
  'salesSpoc',
]);

const VG_NVG_OPTIONS = [
  { id: 'VG', name: 'VG' },
  { id: 'NVG', name: 'NVG' },
];

const EFFORT_LOGGING_OPTIONS = [
  { id: 'Yes', name: 'Yes' },
  { id: 'No', name: 'No' },
];

const DELIVERY_APPROVAL_OPTIONS = [
  { id: 'Yes', name: 'Yes' },
  { id: 'No', name: 'No' },
];

const EFFORT_TYPE_OPTIONS = [
  { id: 'H', name: 'Person Hours' },
  { id: 'D', name: 'Person Days' },
];

const SALES_SPOC_NA_OPTION = {
  id: 'NA',
  name: 'NA',
  empId: '',
  emp_no: 0,
  numericId: 0,
};

const INVESTMENT_SPONSOR_ID = 2;

const toDarpanPlaceholderLabel = (label = '', fieldName = '') => {
  const managerMatch = String(fieldName || '').match(/^pm(\d+)$/);
  if (managerMatch) return `--- Manager ${managerMatch[1]} ---`;

  const normalizedLabel = String(label || '').trim();
  if (!normalizedLabel) return '--- Select ---';

  const titleLabel = normalizedLabel
    .split(' ')
    .map((word) => {
      if (word.toUpperCase() === word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');

  return `--- ${titleLabel} ---`;
};
const SALES_GM_MIN = 0;
const SALES_GM_MAX = 999.99;
const LEGACY_EMPTY_DATE = '9999-01-01';
const REDMINE_ID_MAX = 32767;
const LEGACY_PROJECT_TYPES = {
  [PROJECT_TYPES.FIXED_PRICE]: 'FPP',
  [PROJECT_TYPES.TIME_AND_MATERIAL]: 'T&M',
};

const BASIC_PROJECT_DETAIL_FIELDS = new Set([
  'projectName',
  'projectStatus',
  'vertical',
  'projectGoals',
  'technology',
  'requirements',
  'successMetrics',
]);

const hasPermissionAction = (permissions = {}, moduleName, action) => (
  Array.isArray(permissions?.[moduleName]) && permissions[moduleName].includes(action)
);

const isEnabledFlag = (value) => value === true || value === 1 || value === '1';

const isSuperAdminUser = (userProfile = {}) => {
  if (
    isEnabledFlag(userProfile?.isSuperAdmin)
    || isEnabledFlag(userProfile?.superAdmin)
    || isEnabledFlag(userProfile?.super_admin)
  ) return true;

  const roleDetails = Array.isArray(userProfile?.roleDetails)
    ? userProfile.roleDetails
    : userProfile?.roleDetails
      ? [userProfile.roleDetails]
      : [];

  return roleDetails.some((role) => {
    const roleName = String(role?.roleName || role?.role_name || role?.name || '').trim()
      .toLowerCase();
    return roleName === 'super admin';
  });
};

// ==================== REQUIRED FIELDS ====================
const REQUIRED_TM_FIELDS = [
  'projectName',
  'seniorManager',
  'vertical',
  'poNumber',
  'billingType',
  'salesGMPercent',
  'salesSpoc',
  'vgNvg',
  'effortLoggingInDarpan',
  'customerType',
  'country',
  'region',
  'cloudProvider',
];

const REQUIRED_FP_FIELDS = [
  'projectName',
  'seniorManager',
  'billingType',
  'salesGMPercent',
  'vgNvg',
  'salesSpoc',
  'effortLoggingInDarpan',
  'customerType',
  'cloudProvider',
  'country',
  'region',
  'vertical',
];

const REQUIRED_FIELDS = {
  [PROJECT_TYPES.MASTER]: ['masterProjectName'],
  [PROJECT_TYPES.TIME_AND_MATERIAL]: REQUIRED_TM_FIELDS,
  [PROJECT_TYPES.FIXED_PRICE]: REQUIRED_FP_FIELDS,
};

// ==================== FIELD CONFIGURATIONS ====================
const ProjectFields = [
  { label: 'Master project name', name: 'masterProjectName', type: 'text', placeholder: 'Enter master project name', visibleFor: [PROJECT_TYPES.MASTER] },
  { label: 'Intenal Project', name: 'internalProject', type: 'checkbox', visibleFor: [PROJECT_TYPES.TIME_AND_MATERIAL] },
  { label: 'Project name', name: 'projectName', type: 'text', placeholder: 'Enter project name', visibleFor: COMMON },
  { label: 'Project Status', name: 'projectStatus', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'projectStatus', visibleFor: COMMON },
  { label: 'Vertical', name: 'vertical', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'vertical', visibleFor: COMMON },
  { label: 'Delivery manager', name: 'deliveryManager', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Senior manager', name: 'seniorManager', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Success metrics', name: 'successMetrics', type: 'text', placeholder: 'Enter success metrics', visibleFor: COMMON },
  { label: 'Business / Functional requirements', name: 'requirements', type: 'text', placeholder: 'Enter business / functional requirements', visibleFor: COMMON },
  { label: 'Project goals', name: 'projectGoals', type: 'text', placeholder: 'Enter project goals', visibleFor: COMMON },
  { label: 'Baseline start date', name: 'baselineStartDate', type: 'date', placeholder: SELECT, icon: Calendaricon, visibleFor: COMMON },
  { label: 'Baseline end date', name: 'baselineEndDate', type: 'date', placeholder: SELECT, icon: Calendaricon, visibleFor: COMMON },
  { label: 'Technology', name: 'technology', type: 'autocomplete', placeholder: 'Enter technology', dataSource: 'technology', visibleFor: COMMON },
  { label: 'Actual start date', name: 'actualStartDate', type: 'date', placeholder: SELECT, icon: Calendaricon, visibleFor: COMMON },
  { label: 'Replanned end date', name: 'replannedEndDate', type: 'date', placeholder: SELECT, icon: Calendaricon, visibleFor: COMMON },
  { label: 'Client name', name: 'clientName', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'clientName', visibleFor: COMMON },
  { label: 'Country', name: 'country', type: 'select', placeholder: SELECT, icon: dropdwn, dependsOn: 'region', dataSource: 'country', visibleFor: COMMON },
  { label: 'Region', name: 'region', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'region', visibleFor: COMMON },
  { label: 'Project manager-1', name: 'pm1', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Project manager-2', name: 'pm2', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Project manager-3', name: 'pm3', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Project manager-4', name: 'pm4', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Project manager-5', name: 'pm5', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Project manager-6', name: 'pm6', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Project manager-7', name: 'pm7', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Project manager-8', name: 'pm8', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Project manager-9', name: 'pm9', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Project manager-10', name: 'pm10', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Project manager-11', name: 'pm11', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Project manager-12', name: 'pm12', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Project manager-13', name: 'pm13', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Project manager-14', name: 'pm14', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Project manager-15', name: 'pm15', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Architect', name: 'architect', type: 'multi-autocomplete', placeholder: 'Enter architect name', dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Senior architect', name: 'seniorArchitect', type: 'multi-autocomplete', placeholder: 'Enter architect name', dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'Opportunity Name', name: 'opportunityName', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'opportunity', visibleFor: COMMON },
  { label: 'Zoho Client Name', name: 'zohoClientName', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'zohoClientName', visibleFor: COMMON },
  { label: 'Billing type', name: 'billingType', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'billingType', visibleFor: COMMON },
  { label: 'Billing entity', name: 'billingentity', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'billingEntity', visibleFor: COMMON },
  { label: 'Delivery Methodology', name: 'deliveryMethodology', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'deliveryMethodology', visibleFor: COMMON },
  { label: 'Master project name', name: 'masterProjectName', type: 'autocomplete', placeholder: SELECT, dataSource: 'masterProjectName', visibleFor: COMMON },
  { label: 'Sponsors', name: 'sponsors', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'sponsors', visibleFor: COMMON },
  { label: 'Sponsor name', name: 'sponsorName', type: 'select', placeholder: SELECT, icon: dropdwn, dependsOn: 'sponsors', dataSource: 'sponsorName', visibleFor: COMMON },
  { label: 'Offering', name: 'offering', type: 'multiselect', placeholder: SELECT, icon: dropdwn, hasVerticalSelection: true, dataSource: 'offering', visibleFor: COMMON },
  { label: 'Sales GM%', name: 'salesGMPercent', type: 'text', placeholder: 'Enter Percentage', visibleFor: COMMON },
  { label: 'Sales SPOC', name: 'salesSpoc', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'seniorManager', visibleFor: COMMON },
  { label: 'VG / NVG', name: 'vgNvg', type: 'select', placeholder: SELECT, icon: dropdwn, visibleFor: COMMON },
  { label: 'Effort logging in Darpan', name: 'effortLoggingInDarpan', type: 'select', placeholder: SELECT, icon: dropdwn, visibleFor: COMMON },
  { label: 'DL group ID', name: 'dlGroupId', type: 'text', placeholder: 'Enter ID', visibleFor: COMMON },
  { label: 'Jira project ID', name: 'jiraProjectId', type: 'text', placeholder: 'Enter ID', visibleFor: COMMON },
  { label: 'Jira link', name: 'jiraLink', type: 'text', placeholder: 'Enter link', visibleFor: COMMON },
  { label: 'Wiki link', name: 'projectFolderLink', type: 'text', placeholder: 'Enter wiki link', visibleFor: COMMON },
  { label: 'Customer type', name: 'customerType', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'customerType', visibleFor: COMMON },
  { label: 'PO number', name: 'poNumber', type: 'text', placeholder: 'Enter PO number', visibleFor: [PROJECT_TYPES.TIME_AND_MATERIAL] },
  { label: 'Remarks', name: 'remarks', type: 'text', placeholder: 'Enter remarks', visibleFor: [PROJECT_TYPES.TIME_AND_MATERIAL] },
  { label: 'Project duration', name: 'projectDuration', type: 'select', placeholder: SELECT, icon: dropdwn, visibleFor: [PROJECT_TYPES.FIXED_PRICE] },
  { label: 'Efforts', name: 'efforts', type: 'text', placeholder: 'Enter efforts', visibleFor: [PROJECT_TYPES.FIXED_PRICE] },
  { label: 'Finance approval', name: 'financeApproval', type: 'text', placeholder: 'Enter efforts', visibleFor: [PROJECT_TYPES.FIXED_PRICE] },
  { label: 'Delivery approval', name: 'deliveryApproval', type: 'select', placeholder: 'Enter efforts', icon: dropdwn, visibleFor: [PROJECT_TYPES.FIXED_PRICE] },
  { label: 'No. of resources', name: 'noOfResources', type: 'text', placeholder: 'Enter efforts', visibleFor: [PROJECT_TYPES.FIXED_PRICE] },
  { label: 'Risks issues', name: 'risksIssues', type: 'text', placeholder: 'Enter efforts', visibleFor: [PROJECT_TYPES.FIXED_PRICE] },
  { label: 'Redmine ID', name: 'redmineId', type: 'text', placeholder: 'Enter redmine ID', visibleFor: COMMON },
  { label: 'Cloud Provider', name: 'cloudProvider', type: 'multiselect', placeholder: SELECT, icon: dropdwn, dataSource: 'cloudProvider', visibleFor: COMMON },
  { label: 'Sub billing account', name: 'subBillingAccount', type: 'text', placeholder: 'Enter sub billing account', visibleFor: COMMON },
  { label: 'Billing ID', name: 'billingId', type: 'text', placeholder: 'Enter ID', visibleFor: COMMON },
  { label: 'Revenue split', name: 'revenueSplit', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'revenueSplit', visibleFor: COMMON },
  { label: 'Cost split', name: 'costSplit', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'costSplit', visibleFor: COMMON },
  { label: 'Consumption type', name: 'consumptionType', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'consumptionType', visibleFor: COMMON },
  { label: 'Sales vertical', name: 'salesVertical', type: 'select', placeholder: SELECT, icon: dropdwn, dataSource: 'salesVertical', visibleFor: COMMON },
  { label: 'Hubspot ID', name: 'hubspotId', type: 'text', placeholder: 'Enter hubspot ID', visibleFor: COMMON },
];

// ==================== UTILITY FUNCTIONS ====================

/**
 * Safely parse JSON response data
 */
const parseResponseData = (data) => {
  try {
    return typeof data === 'string' ? JSON.parse(data) : data;
  } catch (error) {
    console.error('JSON Parse Error:', error);
    return data;
  }
};

/**
 * Extract value using multiple possible keys (case-insensitive key matching)
 */
const extractValue = (obj, possibleKeys) => {
  if (!obj || typeof obj !== 'object') return undefined;

  const objectKeys = Object.keys(obj);
  const normalizedKeyMap = objectKeys.reduce((acc, key) => {
    const normalized = String(key).toLowerCase()
      .replace(/[^a-z0-9]/g, '');
    acc[normalized] = key;
    return acc;
  }, {});

  for (const key of possibleKeys) {
    if (obj?.[key] !== undefined && obj[key] !== null) {
      return obj[key];
    }

    const normalizedTarget = String(key).toLowerCase()
      .replace(/[^a-z0-9]/g, '');
    const matchedKey = normalizedKeyMap[normalizedTarget];
    if (matchedKey && obj?.[matchedKey] !== undefined && obj[matchedKey] !== null) {
      return obj[matchedKey];
    }
  }
  return undefined;
};

/**
 * Extract array from nested response structure
 */
const extractArrayFromResponse = (data, possibleKeys) => {
  if (Array.isArray(data)) return data;

  for (const key of possibleKeys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  const sources = [data?.data, data?.result];
  for (const source of sources) {
    if (!source) continue;
    if (Array.isArray(source)) return source;

    for (const key of possibleKeys) {
      if (Array.isArray(source?.[key])) return source[key];
    }
  }

  return [];
};

/**
 * Normalize API response to consistent option format { id, name }
 */
const normalizeOptions = (items, nameKeys, idKeys) => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => {
      if (typeof item === 'string') {
        return { id: item, name: item };
      }

      const name = extractValue(item, nameKeys);
      const id = extractValue(item, idKeys) || name;

      if (!name) return null;

      return { id: String(id), name: String(name) };
    })
    .filter(Boolean)
    .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' }));
};

const isActiveCloudProvider = (provider) => {
  const activeValue = provider?.is_active ?? provider?.isActive;
  if (activeValue === undefined || activeValue === null || activeValue === '') return true;
  return activeValue !== false
    && activeValue !== 0
    && activeValue !== '0'
    && String(activeValue).toLowerCase() !== 'inactive';
};

const dedupeOptions = (options = []) => {
  const seen = new Set();
  return options.filter((option) => {
    const normalizedName = String(option?.name || '').trim()
      .toLowerCase();
    // Deduplicate by label text so repeated names from mapping table appear only once.
    const key = normalizedName || String(option?.id || '').trim()
      .toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const dedupeManagerOptions = (options = []) => {
  const seen = new Set();
  return (Array.isArray(options) ? options : []).filter((option) => {
    const key = String(option?.name || '').trim()
      .toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
const getSettledResponseData = (result) => {
  if (result?.status === 'fulfilled') {
    return result.value?.data;
  }

  return result?.reason;
};

const toInteger = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.trunc(num) : null;
};

const toDecimal = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const normalizeSalesGmPayload = (value) => {
  const normalized = String(value ?? '').trim();
  if (normalized.toUpperCase() === 'NA') {
    return { value: '0', type: 'string' };
  }
  if (normalized === '-') {
    return { value: '0', type: 'other' };
  }
  return {
    value: normalized,
    type: 'number',
  };
};

const toPositiveInteger = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  const intVal = Math.trunc(num);
  return intVal > 0 ? intVal : null;
};

const resolveIntegerId = (value, options = []) => {
  if (value && typeof value === 'object') {
    const objectId = toPositiveInteger(value.id ?? value.value ?? value.region_id ?? value.country_id);
    if (objectId !== null) return objectId;
    const objectName = value.name ?? value.label ?? value.regionName ?? value.countryName;
    return resolveIntegerId(objectName, options);
  }

  const directId = toPositiveInteger(value);
  if (directId !== null) return directId;

  const normalizedValue = String(value || '').trim()
    .toLowerCase();
  if (!normalizedValue) return null;

  const matchedOption = options.find((option) => {
    const optionId = String(option?.id || '').trim()
      .toLowerCase();
    const optionName = String(option?.name || '').trim()
      .toLowerCase();
    return optionId === normalizedValue || optionName === normalizedValue;
  });

  return toPositiveInteger(matchedOption?.id);
};

const resolveOptionName = (value, options = []) => {
  if (value && typeof value === 'object') {
    return String(
      value.name
      || value.label
      || value.full_name
      || value.fullName
      || '',
    ).trim();
  }

  const normalizedValue = String(value || '').trim()
    .toLowerCase();
  if (!normalizedValue) return '';

  const matchedOption = options.find((option) => {
    const optionId = String(option?.id || '').trim()
      .toLowerCase();
    const optionName = String(option?.name || '').trim()
      .toLowerCase();
    return optionId === normalizedValue || optionName === normalizedValue;
  });

  return String(matchedOption?.name || value || '').trim();
};

const resolveManagerEmployeeId = (value, options = []) => {
  if (value && typeof value === 'object') {
    return String(
      value.empId
      ?? value.emp_id
      ?? value.employee_id
      ?? value.id
      ?? '',
    ).trim();
  }

  const matchedOption = findOptionByIdOrName(value, options);
  return String(
    matchedOption?.empId
    ?? matchedOption?.emp_id
    ?? matchedOption?.employee_id
    ?? matchedOption?.id
    ?? value
    ?? '',
  ).trim();
};

const resolveManagerOption = (value, options = []) => {
  if (value && typeof value === 'object') {
    return findOptionByIdOrName(value, options) || value;
  }
  return findOptionByIdOrName(value, options);
};

const resolveManagerNumericId = (value, options = []) => {
  if (value && typeof value === 'object') {
    return toPositiveInteger(
      value.numericId
      ?? value.emp_no
      ?? value.value,
    );
  }
  const matchedOption = findOptionByIdOrName(value, options);
  return toPositiveInteger(
    matchedOption?.numericId
    ?? matchedOption?.emp_no
    ?? matchedOption?.value,
  );
};

const resolveSalesSpocNumericId = (value, options = []) => {
  if (String(value?.id ?? value ?? '').trim()
    .toUpperCase() === SALES_SPOC_NA_OPTION.id) {
    return 0;
  }

  return resolveManagerNumericId(value, options);
};

const findOptionByIdOrName = (value, options = []) => {
  if (value && typeof value === 'object') {
    const normalizedObjectId = String(value.id ?? value.value ?? '').trim()
      .toLowerCase();
    const normalizedObjectEmpId = String(
      value.empId ?? value.emp_id ?? value.employee_id ?? '',
    ).trim()
      .toLowerCase();
    const normalizedObjectEmpNo = String(
      value.numericId ?? value.emp_no ?? '',
    ).trim()
      .toLowerCase();
    const normalizedObjectName = String(value.name ?? value.label ?? '').trim()
      .toLowerCase();
    return options.find((option) => {
      const optionId = String(option?.id || '').trim()
        .toLowerCase();
      const optionEmpId = String(
        option?.empId ?? option?.emp_id ?? option?.employee_id ?? '',
      ).trim()
        .toLowerCase();
      const optionEmpNo = String(
        option?.numericId ?? option?.emp_no ?? '',
      ).trim()
        .toLowerCase();
      const optionName = String(option?.name || '').trim()
        .toLowerCase();
      return (
        (normalizedObjectId && optionId === normalizedObjectId)
        || (normalizedObjectEmpId && optionEmpId === normalizedObjectEmpId)
        || (normalizedObjectEmpNo && optionEmpNo === normalizedObjectEmpNo)
        || (normalizedObjectName && optionName === normalizedObjectName)
      );
    }) || null;
  }

  const normalizedValue = String(value || '').trim()
    .toLowerCase();
  if (!normalizedValue) return null;

  return options.find((option) => {
    const optionId = String(option?.id || '').trim()
      .toLowerCase();
    const optionEmpId = String(
      option?.empId ?? option?.emp_id ?? option?.employee_id ?? '',
    ).trim()
      .toLowerCase();
    const optionEmpNo = String(
      option?.numericId ?? option?.emp_no ?? '',
    ).trim()
      .toLowerCase();
    const optionName = String(option?.name || '').trim()
      .toLowerCase();
    return (
      optionId === normalizedValue
      || optionEmpId === normalizedValue
      || optionEmpNo === normalizedValue
      || optionName === normalizedValue
    );
  }) || null;
};

const normalizeSingleSelectValue = (value, options = []) => {
  const matchedOption = findOptionByIdOrName(value, options);
  return matchedOption ? String(matchedOption.id) : null;
};

const normalizeMultiSelectValues = (values, options = []) => {
  const normalizedValues = Array.isArray(values) ? values : parseOfferingValues(values);
  if (!Array.isArray(options) || options.length === 0) {
    return Array.from(
      new Set(
        normalizedValues
          .map((value) => String(value || '').trim())
          .filter(Boolean),
      ),
    );
  }

  const resolvedIds = normalizedValues
    .map((value) => normalizeSingleSelectValue(value, options))
    .filter(Boolean);

  return Array.from(new Set(resolvedIds));
};

const normalizeEmployeeMultiSelectRefs = (values, options = [], refKey = 'emp_no') => {
  const normalizedValues = Array.isArray(values) ? values : parseOfferingValues(values);
  return Array.from(
    new Set(
      normalizedValues
        .map((value) => resolveManagerOption(value, options))
        .map((option) => {
          if (!option) return '';
          if (refKey === 'emp_id') {
            return String(
              option.empId
              ?? option.emp_id
              ?? option.employee_id
              ?? option.id
              ?? '',
            ).trim();
          }
          return String(
            option.numericId
            ?? option.emp_no
            ?? option.value
            ?? '',
          ).trim();
        })
        .filter(Boolean),
    ),
  );
};

const resolveAutocompleteSelection = (fieldName, rawInputValue, options = []) => {
  const matchedOption = findOptionByIdOrName(rawInputValue, options);
  if (!matchedOption) return null;
  return {
    fieldName,
    id: String(matchedOption.id),
    name: String(matchedOption.name || ''),
  };
};

const toBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  const normalized = String(value || '').trim()
    .toLowerCase();
  return ['yes', 'true', '1', 'vg'].includes(normalized);
};

const toLegacyFlag = (value, fallback = '') => {
  if (value === null || value === undefined || value === '') return fallback;
  return toBoolean(value) ? '1' : '0';
};

const getProjectTypeId = (projectType) => {
  if (projectType === PROJECT_TYPES.FIXED_PRICE) return 1;
  if (projectType === PROJECT_TYPES.TIME_AND_MATERIAL) return 2;
  return 3;
};

const getProjectTypeById = (projectTypeId) => {
  const id = Number(projectTypeId);
  if (id === 1) return PROJECT_TYPES.FIXED_PRICE;
  if (id === 2) return PROJECT_TYPES.TIME_AND_MATERIAL;
  return PROJECT_TYPES.MASTER;
};

const normalizeProjectTypeValue = (value = '') => {
  const normalized = String(value || '').trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

  if (['TM', 'TIMEANDMATERIAL', 'TIMEMATERIAL'].includes(normalized)) {
    return PROJECT_TYPES.TIME_AND_MATERIAL;
  }
  if (['FP', 'FPP', 'FIXEDPRICE'].includes(normalized)) {
    return PROJECT_TYPES.FIXED_PRICE;
  }
  return '';
};

const toDateOnly = (value) => {
  if (!value) return '';
  return String(value).split('T')[0];
};

const toLegacyDateOnly = (value) => toDateOnly(value || '') || LEGACY_EMPTY_DATE;
const toPrefillDateOnly = (value) => {
  const normalized = toDateOnly(value);
  return normalized === LEGACY_EMPTY_DATE ? '' : normalized;
};

const normalizeLegacyEffortType = (value, fallback = 'D') => {
  const normalized = String(value || '').trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '');

  if (['H', 'HR', 'HOUR', 'HOURS', 'PERSONHOUR', 'PERSONHOURS'].includes(normalized)) return 'H';
  if (['D', 'DAY', 'DAYS', 'PERSONDAY', 'PERSONDAYS'].includes(normalized)) return 'D';
  return fallback;
};

const getServerMessage = (responseOrError, fallbackMessage = 'Request failed') => {
  const payload = responseOrError?.data || responseOrError;
  return (
    payload?.response?.data?.message
    || payload?.message
    || fallbackMessage
  );
};

const debugLog = (...args) => {
  if (process.env.NODE_ENV !== 'production') {
    console.debug(...args);
  }
};

const readPathValue = (obj, path) => {
  if (!obj || !path) return undefined;
  return String(path)
    .split('.')
    .reduce((acc, key) => (acc === undefined || acc === null ? undefined : acc[key]), obj);
};

const getFirstValueByPaths = (obj, paths = []) => {
  for (const path of paths) {
    const value = readPathValue(obj, path);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return undefined;
};

const getFirstNonEmptyValueByPaths = (obj, paths = []) => {
  for (const path of paths) {
    const value = readPathValue(obj, path);
    if (Array.isArray(value)) {
      if (value.length > 0) return value;
      continue;
    }
    if (value && typeof value === 'object') {
      if (Object.keys(value).length > 0) return value;
      continue;
    }
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return undefined;
};

const findFirstValueRecursive = (source, paths, visited = new WeakSet()) => {
  if (source === null || source === undefined) return undefined;

  const directValue = getFirstValueByPaths(source, paths);
  if (directValue !== undefined && directValue !== null && directValue !== '') {
    return directValue;
  }

  if (typeof source !== 'object') return undefined;
  if (visited.has(source)) return undefined;
  visited.add(source);

  if (Array.isArray(source)) {
    for (const item of source) {
      const nestedValue = findFirstValueRecursive(item, paths, visited);
      if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '') {
        return nestedValue;
      }
    }
    return undefined;
  }

  for (const value of Object.values(source)) {
    const nestedValue = findFirstValueRecursive(value, paths, visited);
    if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '') {
      return nestedValue;
    }
  }

  return undefined;
};

const getSalesGmPrefillValue = (data) => {
  const salesGmType = String(findFirstValueRecursive(data, [
    'sales_gross_margin_type',
    'project.sales_gross_margin_type',
    'salesGrossMarginType',
    'project.salesGrossMarginType',
  ]) || '').trim()
    .toLowerCase();

  if (salesGmType === 'string') return 'NA';
  if (salesGmType === 'other') return '-';

  const canonicalPaths = [
    'sales_gross_margin',
    'project.sales_gross_margin',
    // Keep the legacy GP field as a fallback only. Some project-details payloads
    // default it to 0 even when sales_gross_margin has the persisted SGM value.
    'gp_precent',
    'project.gp_precent',
  ];
  const derivedPaths = [
    'sgm',
    'gmSummary.sgm',
    'project.gmSummary.sgm',
    'salesGMPercent',
    'project.salesGMPercent',
  ];

  const canonicalValue = findFirstValueRecursive(data, canonicalPaths);
  if (canonicalValue !== undefined && canonicalValue !== null && canonicalValue !== '') {
    return canonicalValue;
  }

  const derivedValue = findFirstValueRecursive(data, derivedPaths);
  if (derivedValue !== undefined && derivedValue !== null && derivedValue !== '') {
    return derivedValue;
  }

  return '';
};

const resolveProjectTypeFromData = (data) => {
  const effortType = normalizeProjectTypeValue(getFirstValueByPaths(data, [
    'effort_type',
    'project.effort_type',
    'projectType',
    'project.projectType',
    'project_type',
    'project.project_type',
  ]));
  if (effortType) return effortType;

  const projectTypeIdRaw = getFirstValueByPaths(data, [
    'project_type_id',
    'project.project_type_id',
    'projectTypeId',
    'project.projectTypeId',
  ]);
  const projectTypeId = Number(projectTypeIdRaw);
  if (projectTypeId === 1) return PROJECT_TYPES.FIXED_PRICE;
  if (projectTypeId === 2) return PROJECT_TYPES.TIME_AND_MATERIAL;

  const projectTypeRaw = normalizeProjectTypeValue(getFirstValueByPaths(data, [
    'project_type_id',
    'project.project_type_id',
  ]));
  if (projectTypeRaw) return projectTypeRaw;

  return PROJECT_TYPES.MASTER;
};

const getProjectTypeSelectionFromQuery = ({ variant = '', projectType = '' } = {}) => {
  const normalizedVariant = String(variant || '').trim()
    .toLowerCase();
  if (normalizedVariant === 'tm') return 'Add new TM project';
  if (normalizedVariant === 'fpp' || normalizedVariant === 'fp') return 'Add new FP project';

  const normalizedProjectType = String(projectType || '').trim()
    .toLowerCase();
  if (!normalizedProjectType) return '';
  if (normalizedProjectType.includes('tm') || normalizedProjectType.includes('t&m')) return 'Add new TM project';
  if (
    normalizedProjectType.includes('fp')
    || normalizedProjectType.includes('fpp')
    || normalizedProjectType.includes('fixed')
  ) return 'Add new FP project';
  if (normalizedProjectType.includes('master')) return 'Add new Master project';
  return '';
};

const getProjectTypeFromSelection = (selection = '') => {
  const normalized = String(selection || '').trim()
    .toLowerCase();
  if (normalized.includes('t&m') || normalized.includes('tm')) return PROJECT_TYPES.TIME_AND_MATERIAL;
  if (normalized.includes('fp') || normalized.includes('fixed')) return PROJECT_TYPES.FIXED_PRICE;
  if (normalized.includes('master')) return PROJECT_TYPES.MASTER;
  return '';
};

const toYesNo = (value) => (toBoolean(value) ? 'Yes' : 'No');
const hasSelectedValue = (value) => (
  value !== undefined
  && value !== null
  && String(value).trim() !== ''
);

const parseOfferingValues = (rawValue) => {
  if (Array.isArray(rawValue)) {
    return rawValue
      .flatMap((item) => parseOfferingValues(item))
      .filter(Boolean);
  }
  if (rawValue && typeof rawValue === 'object') {
    return parseOfferingValues(
      rawValue.project_offering
      ?? rawValue.projectOffering
      ?? rawValue.project_offering_id
      ?? rawValue.projectOfferingId
      ?? rawValue.offering
      ?? rawValue.offeringId
      ?? rawValue.id
      ?? rawValue.name,
    );
  }
  if (typeof rawValue === 'string') {
    return rawValue
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
    return [String(rawValue).trim()].filter(Boolean);
  }
  return [];
};

const parseEmployeeValues = (rawValue) => {
  if (Array.isArray(rawValue)) {
    return rawValue
      .flatMap((item) => parseEmployeeValues(item))
      .filter(Boolean);
  }

  if (rawValue && typeof rawValue === 'object') {
    const nestedValue = rawValue.project_architects_details
      ?? rawValue.project_sr_architects_details
      ?? rawValue.project_architects
      ?? rawValue.project_sr_architects
      ?? rawValue.project_senior_architect
      ?? rawValue.architect
      ?? rawValue.seniorArchitect;

    if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '') {
      return parseEmployeeValues(nestedValue);
    }

    const fullName = rawValue.full_name
      ?? rawValue.fullName
      ?? [rawValue.first_name ?? rawValue.firstName, rawValue.last_name ?? rawValue.lastName]
        .filter(Boolean)
        .join(' ')
        .trim()
      ?? rawValue.name
      ?? rawValue.label;

    if (fullName) {
      return [String(fullName).trim()].filter(Boolean);
    }

    const preferredValue = rawValue.emp_id
      ?? rawValue.empId
      ?? rawValue.employee_id
      ?? rawValue.employeeId
      ?? rawValue.id
      ?? rawValue.emp_no
      ?? rawValue.numericId
      ?? rawValue.value;

    return preferredValue !== undefined && preferredValue !== null && preferredValue !== ''
      ? [String(preferredValue).trim()].filter(Boolean)
      : [];
  }

  return parseOfferingValues(rawValue);
};

const mapProjectDetailsToFormData = (data) => ({
  internalProject: toBoolean(getFirstValueByPaths(data, ['internal_project', 'internalProject'])),
  projectName: String(getFirstValueByPaths(data, ['project.projectName', 'project_name', 'projectName']) || ''),
  projectStatus: String(getFirstValueByPaths(data, ['project_status_id', 'projectStatusId', 'status_id', 'statusId']) || ''),
  projectGoals: String(getFirstValueByPaths(data, ['project_description', 'project.projectDescription', 'projectGoals']) || ''),
  opportunityName: String(getFirstValueByPaths(data, ['opp_id', 'opportunity_id', 'project.opportunityId', 'opportunityId']) || ''),
  deliveryManager: String(getFirstValueByPaths(data, [
    'delivery_manager_id',
    'deliveryManager.emp_id',
    'deliveryManager.employee_id',
    'deliveryManager.emp_no',
    'deliveryManager.id',
    'deliveryManager',
  ]) || ''),
  financeApproval: toYesNo(getFirstValueByPaths(data, ['finance_approval', 'project.financeApproval'])),
  risksIssues: toYesNo(getFirstValueByPaths(data, ['risks_issues', 'project.risksIssues'])),
  salesGMPercent: String(getSalesGmPrefillValue(data) ?? ''),
  noOfResources: String(getFirstValueByPaths(data, ['no_resource', 'project.noResource']) || ''),
  baselineStartDate: toDateOnly(getFirstValueByPaths(data, ['start_date', 'base_start_date', 'project.baseStartDate'])),
  baselineEndDate: toDateOnly(getFirstValueByPaths(data, ['end_date', 'base_end_date', 'project.baseEndDate'])),
  actualStartDate: toPrefillDateOnly(getFirstValueByPaths(data, ['actual_start_date', 'actual_start', 'actualStartDate', 'project.actualStartDate'])),
  replannedEndDate: toPrefillDateOnly(getFirstValueByPaths(data, ['replanned_end_date', 'replanned_end', 'replannedEndDate', 'project.replannedEndDate'])),
  projectDuration: normalizeLegacyEffortType(getFirstValueByPaths(data, [
    'effort_type',
    'project.effort_type',
  ]), ''),
  efforts: String(getFirstValueByPaths(data, [
    'effort',
    'project.effort',
    'efforts',
    'project.efforts',
    'estimated_month',
    'project.estimated_month',
    'project.estimatedMonth',
  ]) || ''),
  poNumber: String(getFirstValueByPaths(data, ['po_number', 'project.poNumber']) || ''),
  remarks: String(getFirstValueByPaths(data, ['remarks', 'project.remarks']) || ''),
  vertical: String(getFirstValueByPaths(data, ['project_vertical', 'vertical', 'vertical_id', 'project.verticalId']) || ''),
  seniorManager: String(getFirstValueByPaths(data, ['prog_manager_num', 'senior_manager', 'prog_manager_id', 'project.seniorManager']) || ''),
  pm1: String(getFirstValueByPaths(data, ['manager_id', 'manager_1', 'project.manager1']) || ''),
  pm2: String(getFirstValueByPaths(data, ['manager_2', 'project.manager2']) || ''),
  pm3: String(getFirstValueByPaths(data, ['manager_3', 'project.manager3']) || ''),
  pm4: String(getFirstValueByPaths(data, ['manager_4', 'project.manager4']) || ''),
  pm5: String(getFirstValueByPaths(data, ['manager_5', 'project.manager5']) || ''),
  pm6: String(getFirstValueByPaths(data, ['manager_6', 'project.manager6']) || ''),
  pm7: String(getFirstValueByPaths(data, ['manager_7', 'project.manager7']) || ''),
  pm8: String(getFirstValueByPaths(data, ['manager_8', 'project.manager8']) || ''),
  pm9: String(getFirstValueByPaths(data, ['manager_9', 'project.manager9']) || ''),
  pm10: String(getFirstValueByPaths(data, ['manager_10', 'project.manager10']) || ''),
  pm11: String(getFirstValueByPaths(data, ['manager_11', 'project.manager11']) || ''),
  pm12: String(getFirstValueByPaths(data, ['manager_12', 'project.manager12']) || ''),
  pm13: String(getFirstValueByPaths(data, ['manager_13', 'project.manager13']) || ''),
  pm14: String(getFirstValueByPaths(data, ['manager_14', 'project.manager14']) || ''),
  pm15: String(getFirstValueByPaths(data, ['manager_15', 'project.manager15']) || ''),
  clientName: String(getFirstValueByPaths(data, ['client_id', 'project.clientId']) || ''),
  zohoClientName: String(getFirstValueByPaths(data, ['zoho_client_id', 'zohoClientId', 'zohoClientName', 'zoho_client_name', 'zohoClient']) || ''),
  requirements: String(getFirstValueByPaths(data, ['business_requirements', 'requirements']) || ''),
  deliveryMethodology: String(getFirstValueByPaths(data, ['methodology', 'delivery_methodology', 'deliveryMethodology']) || ''),
  billingType: String(getFirstValueByPaths(data, ['billing_type', 'billingType']) || ''),
  sponsors: String(getFirstValueByPaths(data, ['project_sponsor', 'projectSponsor', 'sponsor_id']) || ''),
  sponsorName: String(getFirstValueByPaths(data, ['project_partner', 'projectPartner', 'partner_id']) || ''),
  successMetrics: String(getFirstValueByPaths(data, ['success_metrics', 'successMetrics']) || ''),
  offering: parseOfferingValues(getFirstValueByPaths(data, [
    'project_offering',
    'project_offering_id',
    'projectOffering',
    'projectOfferingId',
    'projectOfferingIds',
    'offering',
    'offeringId',
    'offeringIds',
  ])),
  architect: parseEmployeeValues(getFirstNonEmptyValueByPaths(data, [
    'project_architects',
    'project_architects_details',
    'architect',
  ])),
  seniorArchitect: parseEmployeeValues(getFirstNonEmptyValueByPaths(data, [
    'project_sr_architects',
    'project_sr_architects_details',
    'project_senior_architect',
    'seniorArchitect',
  ])),
  salesSpoc: String(getFirstValueByPaths(data, ['sales_spoc', 'salesSpoc']) ?? '') === '0'
    ? SALES_SPOC_NA_OPTION.id
    : String(getFirstValueByPaths(data, ['sales_spoc', 'salesSpoc']) || ''),
  jiraProjectId: String(getFirstValueByPaths(data, ['jira_project_id', 'jiraProjectId']) || ''),
  vgNvg: toBoolean(getFirstValueByPaths(data, ['vg_nvg_id', 'vgNvg'])) ? 'VG' : 'NVG',
  deliveryApproval: toYesNo(getFirstValueByPaths(data, ['delivery_approval', 'deliveryApproval'])),
  effortLoggingInDarpan: toYesNo(getFirstValueByPaths(data, ['effort_logging_in_darpan', 'effortLoggingInDarpan'])),
  jiraLink: String(getFirstValueByPaths(data, ['jira_link', 'jiraLink']) || ''),
  dlGroupId: String(getFirstValueByPaths(data, ['project_dl', 'dl_group_id', 'dlGroupId']) || ''),
  projectFolderLink: String(getFirstValueByPaths(data, ['drive_link', 'projectFolderLink']) || ''),
  redmineId: String(getFirstValueByPaths(data, ['redmine_id', 'redmineId']) || ''),
  cloudProvider: parseOfferingValues(getFirstValueByPaths(data, ['cloud_provider', 'cloudProvider'])),
  customerType: String(getFirstValueByPaths(data, ['customer_type_id', 'customerType']) || ''),
  subBillingAccount: String(getFirstValueByPaths(data, ['sub_billing_account', 'subBillingAccount']) || ''),
  billingId: String(getFirstValueByPaths(data, ['billing_id', 'billingId']) || ''),
  hubspotId: String(getFirstValueByPaths(data, ['hubspot_id', 'hubspotId']) || ''),
  technology: String(getFirstValueByPaths(data, ['technology_id', 'tech_id', 'techId']) || ''),
  revenueSplit: String(getFirstValueByPaths(data, ['revenue_split_id', 'revenueSplit']) || ''),
  costSplit: String(getFirstValueByPaths(data, ['cost_split_id', 'costSplit']) || ''),
  consumptionType: String(getFirstValueByPaths(data, ['consumption_id', 'consumptionType']) || ''),
  salesVertical: String(getFirstValueByPaths(data, ['sales_vertical_id', 'salesVertical']) || ''),
  billingentity: String(getFirstValueByPaths(data, ['billing_entity_id', 'billingEntity']) || ''),
  projectTypeId: String(getFirstValueByPaths(data, ['project_type_id', 'projectTypeId']) || ''),
  region: String(getFirstValueByPaths(data, ['region_id', 'region_name', 'regionName', 'region']) || ''),
  country: String(getFirstValueByPaths(data, ['country_id', 'country_name', 'countryName', 'country']) || ''),
  masterProjectName: String(
    getFirstValueByPaths(data, [
      'master_project_id',
      'masterProjectId',
      'project.master_project_id',
      'project.masterProjectId',
      'masterProjectName',
      'master_project_name',
      'project.masterProjectName',
      'project.master_project_name',
    ]) || '',
  ),
});

const getPrefillProjectData = (payload) => {
  const direct = payload?.data ?? payload?.result ?? payload;
  if (Array.isArray(direct)) return direct[0] || null;
  if (!direct || typeof direct !== 'object') return null;

  if (Array.isArray(direct?.rows)) return direct.rows[0] || null;
  if (Array.isArray(direct?.projectOverview)) return direct.projectOverview[0] || null;
  if (Array.isArray(direct?.projectDetails)) return direct.projectDetails[0] || null;
  return direct;
};

const getProjectTechnologyRows = (payload) => {
  const direct = payload?.data ?? payload?.result ?? payload;
  if (Array.isArray(direct)) return direct;
  if (!direct || typeof direct !== 'object') return [];

  if (Array.isArray(direct?.projectTechnologies)) return direct.projectTechnologies;
  if (Array.isArray(direct?.projectTechnologyList)) return direct.projectTechnologyList;
  if (Array.isArray(direct?.rows)) return direct.rows;
  if (direct?.projectTechnology) return [direct.projectTechnology];

  return [];
};

const isNoDataResponse = (payload) => {
  const statusCode = payload?.response?.status ?? payload?.statusCode ?? payload?.status;
  const errorCode = payload?.response?.data?.errorCode ?? payload?.errorCode;

  return Number(statusCode) === 441 || errorCode === 'NO_DATA_FOUND';
};

const buildProjectDetailsPayload = ({
  formData,
  currentProjectType,
  masterData,
  countryIdByName = {},
}) => {
  const projectTypeFromFormId = getProjectTypeById(
    getFirstValueByPaths(formData, ['projectTypeId', 'project_type_id']),
  );
  const effectiveProjectType = (projectTypeFromFormId === PROJECT_TYPES.MASTER ? '' : projectTypeFromFormId)
    || currentProjectType;
  const baseStartDate = toLegacyDateOnly(formData.baselineStartDate);
  const baseEndDate = toLegacyDateOnly(formData.baselineEndDate);
  const actualStartDate = toLegacyDateOnly(formData.actualStartDate);
  const replannedEndDate = toLegacyDateOnly(formData.replannedEndDate);
  const offeringValues = Array.isArray(formData.offering) ? formData.offering : [];
  const normalizedOfferingIds = offeringValues
    .map((value) => resolveIntegerId(value, masterData?.offering))
    .filter((value) => value !== null && value !== undefined)
    .map(String);
  const projectOfferingCsv = Array.from(new Set(normalizedOfferingIds)).join(',');
  const resolvedVerticalId = resolveIntegerId(formData.vertical, masterData?.vertical);
  const resolvedCountryId = resolveIntegerId(formData.country, masterData?.country)
    ?? countryIdByName[String(formData.country || '').trim()
      .toLowerCase()]
    ?? null;
  const managerOptions = masterData?.seniorManager || [];
  const architectValues = normalizeEmployeeMultiSelectRefs(formData.architect, managerOptions, 'emp_no');
  const seniorArchitectValues = normalizeEmployeeMultiSelectRefs(formData.seniorArchitect, managerOptions, 'emp_no');
  const cloudProviderValues = normalizeMultiSelectValues(formData.cloudProvider);
  const architectCsv = architectValues.join(',');
  const seniorArchitectCsv = seniorArchitectValues.join(',');
  const cloudProviderCsv = cloudProviderValues.join(',');
  const resolvedProjectTypeId = getProjectTypeId(effectiveProjectType);
  const deliveryManagerEmpNo = resolveManagerNumericId(formData.deliveryManager, managerOptions);
  const salesSpocEmpNo = resolveSalesSpocNumericId(formData.salesSpoc, managerOptions);
  const programManagerEmpNo = resolveManagerNumericId(formData.seniorManager, managerOptions);
  const isFixedPriceProject = effectiveProjectType === PROJECT_TYPES.FIXED_PRICE;
  const salesGmPayload = normalizeSalesGmPayload(formData.salesGMPercent);
  const resolvedOpportunityId = resolveIntegerId(formData.opportunityName, masterData?.opportunity) ?? 0;
  const commonPayload = {
    project_name: formData.projectName || '',
    project_description: formData.projectGoals || '',
    opportunity_id: resolvedOpportunityId,
    finance_approval: isFixedPriceProject ? toLegacyFlag(formData.financeApproval) : '',
    risks_issues: isFixedPriceProject ? toLegacyFlag(formData.risksIssues) : '',
    gp_precent: toDecimal(salesGmPayload.value),
    no_resource: isFixedPriceProject ? toInteger(formData.noOfResources) : 0,
    base_start_date: baseStartDate,
    base_end_date: baseEndDate,
    actual_start: actualStartDate,
    replanned_end: replannedEndDate,
    actualStartDate,
    replannedEndDate,
    estimated_month: isFixedPriceProject ? toInteger(formData.efforts) : 0,
    effort: String(formData.efforts || '').trim(),
    effort_type: effectiveProjectType,
    po_number: formData.poNumber || '',
    remarks: formData.remarks || '',
    senior_manager: resolveManagerEmployeeId(formData.seniorManager, managerOptions),
    manager_1: resolveManagerEmployeeId(formData.pm1, managerOptions),
    project_status_id: resolveIntegerId(formData.projectStatus, masterData?.projectStatus) ?? 1,
    status_id: resolveIntegerId(formData.projectStatus, masterData?.projectStatus) ?? 1,
    business_requirements: formData.requirements || '',
    prog_manager_num: programManagerEmpNo,
    project_vertical: resolveOptionName(formData.vertical, masterData?.vertical),
    start_date: baseStartDate,
    end_date: baseEndDate,
    actual_start_date: actualStartDate,
    replanned_end_date: replannedEndDate,
    manager_id: resolveManagerEmployeeId(formData.pm1, managerOptions),
    manager_2: resolveManagerEmployeeId(formData.pm2, managerOptions),
    manager_3: resolveManagerEmployeeId(formData.pm3, managerOptions),
    manager_4: resolveManagerEmployeeId(formData.pm4, managerOptions),
    manager_5: resolveManagerEmployeeId(formData.pm5, managerOptions),
    manager_6: resolveManagerEmployeeId(formData.pm6, managerOptions),
    manager_7: resolveManagerEmployeeId(formData.pm7, managerOptions),
    manager_8: resolveManagerEmployeeId(formData.pm8, managerOptions),
    manager_9: resolveManagerEmployeeId(formData.pm9, managerOptions),
    manager_10: resolveManagerEmployeeId(formData.pm10, managerOptions),
    manager_11: resolveManagerEmployeeId(formData.pm11, managerOptions),
    manager_12: resolveManagerEmployeeId(formData.pm12, managerOptions),
    manager_13: resolveManagerEmployeeId(formData.pm13, managerOptions),
    manager_14: resolveManagerEmployeeId(formData.pm14, managerOptions),
    manager_15: resolveManagerEmployeeId(formData.pm15, managerOptions),
    opp_id: resolvedOpportunityId,
    client_id: resolveIntegerId(formData.clientName, masterData?.clientName),
    zoho_client_id: resolveIntegerId(formData.zohoClientName, masterData?.zohoClientName),
    hubspot_id: String(formData.hubspotId || '').trim() || '0',
    methodology: resolveIntegerId(formData.deliveryMethodology, masterData?.deliveryMethodology),
    billing_type: resolveIntegerId(
      formData.billingType,
      effectiveProjectType === PROJECT_TYPES.FIXED_PRICE ? masterData?.fpBillingType : masterData?.tmBillingType,
    ),
    project_sponsor: resolveIntegerId(formData.sponsors, masterData?.sponsors),
    project_partner: resolveIntegerId(formData.sponsorName, masterData?.sponsorName),
    prog_manager_id: resolveManagerNumericId(formData.seniorManager, masterData?.seniorManager),
    success_metrics: formData.successMetrics || '',
    project_offering: projectOfferingCsv || '',
    project_architects: architectCsv || '',
    project_sr_architects: seniorArchitectCsv || '',
    sales_gross_margin: salesGmPayload.value,
    sales_gross_margin_type: salesGmPayload.type,
    jira_project_id: formData.jiraProjectId || '',
    vg_nvg_id: toBoolean(formData.vgNvg) ? 1 : 0,
    effort_logging_in_darpan: toBoolean(formData.effortLoggingInDarpan) ? 1 : 0,
    project_dl: String(formData.dlGroupId || '').trim(),
    jira_link: formData.jiraLink || '',
    drive_link: formData.projectFolderLink || '',
    customer_type_id: resolveIntegerId(formData.customerType, masterData?.customerType),
    sub_billing_account: formData.subBillingAccount || '',
    billing_id: String(formData.billingId || '').trim(),
    revenue_split_id: resolveIntegerId(formData.revenueSplit, masterData?.revenueSplit),
    cost_split_id: resolveIntegerId(formData.costSplit, masterData?.costSplit),
    consumption_id: resolveIntegerId(formData.consumptionType, masterData?.consumptionType),
    sales_vertical_id: resolveIntegerId(formData.salesVertical, masterData?.salesVertical),
    billing_entity_id: resolveIntegerId(formData.billingentity, masterData?.billingEntity),
    region_id: resolveIntegerId(formData.region, masterData?.region),
    country_id: resolvedCountryId,
    master_project_id: resolveIntegerId(formData.masterProjectName, masterData?.masterProjectName),
    delivery_manager_id: deliveryManagerEmpNo,
    sales_spoc: salesSpocEmpNo,
    cloud_provider: cloudProviderCsv || '',
    tech_id: resolveIntegerId(formData.technology, masterData?.technology),
    // Current schema still requires these ids even though legacy create keyed off names.
    vertical_id: resolvedVerticalId,
    project_type_id: resolvedProjectTypeId,
  };

  if (effectiveProjectType === PROJECT_TYPES.FIXED_PRICE) {
    return {
      ...commonPayload,
      effort_type: normalizeLegacyEffortType(formData.projectDuration, 'H'),
      estimated_month: toInteger(formData.efforts) ?? 0,
      finance_approval: toLegacyFlag(formData.financeApproval),
      delivery_approval: toLegacyFlag(formData.deliveryApproval),
      risks_issues: toLegacyFlag(formData.risksIssues),
      no_resource: toInteger(formData.noOfResources),
      redmine_id: toInteger(formData.redmineId),
      project_type: LEGACY_PROJECT_TYPES[PROJECT_TYPES.FIXED_PRICE],
    };
  }

  return {
    ...commonPayload,
    effort_type: 'D',
    po_number: formData.poNumber || '',
    remarks: formData.remarks || '',
    redmine_id: toInteger(formData.redmineId),
    project_type: LEGACY_PROJECT_TYPES[PROJECT_TYPES.TIME_AND_MATERIAL],
  };
};

// ==================== MAIN COMPONENT ====================

const AddProject = () => {
  const { userProfile } = useAuth();
  const { snackbarOpen } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const projectIdFromQuery = queryParams.get('projectId')
    || queryParams.get('project_id')
    || queryParams.get('projectSeqId')
    || queryParams.get('project_seq_id')
    || '';
  const projectSeqIdFromQuery = queryParams.get('projectSeqId')
    || queryParams.get('project_seq_id')
    || '';
  const modeFromQuery = String(queryParams.get('mode') || '').trim()
    .toLowerCase();
  const isEditableFromQuery = String(queryParams.get('isEditable') || 'true').trim()
    .toLowerCase() !== 'false';
  const variantFromQuery = String(queryParams.get('variant') || '').trim();
  const projectTypeFromQuery = String(queryParams.get('projectType') || '').trim();
  const isEditMode = Boolean(projectIdFromQuery) && (modeFromQuery === 'edit' || modeFromQuery === '');
  const permissions = userProfile?.permissions || {};
  const isSuperAdmin = isSuperAdminUser(userProfile);
  const canCreateProject = isSuperAdmin
    || hasPermissionAction(permissions, MODULE_NAMES.DELIVERY_ADD_PROJECTS, 'create');
  const canUpdateProject = isSuperAdmin
    || hasPermissionAction(permissions, MODULE_NAMES.DELIVERY_PROJECT_DETAILS_EDIT, 'update')
    || hasPermissionAction(permissions, MODULE_NAMES.DELIVERY_PROJECT_MANAGER_DETAILS_EDIT, 'update');
  const canEditDetailProjectFields = isSuperAdmin || hasPermissionAction(
    permissions,
    MODULE_NAMES.DELIVERY_PROJECT_DETAILS_EDIT,
    'update',
  );
  const canEditManagerBasicProjectFields = isSuperAdmin || hasPermissionAction(
    permissions,
    MODULE_NAMES.DELIVERY_PROJECT_MANAGER_DETAILS_EDIT,
    'update',
  );
  const canEditAnyProjectFields = canEditDetailProjectFields || canEditManagerBasicProjectFields;

  const canCreateMasterProject = canCreateProject;

  const selectedProjectTypeFromQuery = useMemo(
    () => getProjectTypeSelectionFromQuery({ variant: variantFromQuery, projectType: projectTypeFromQuery }),
    [projectTypeFromQuery, variantFromQuery],
  );

  // ==================== STATE MANAGEMENT ====================
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [selectedProjectType, setSelectedProjectType] = useState(
    selectedProjectTypeFromQuery || 'Add new Master project',
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isPrefillLoading, setIsPrefillLoading] = useState(Boolean(isEditMode && projectIdFromQuery));
  const [prefillError, setPrefillError] = useState('');
  const [lockedProjectType, setLockedProjectType] = useState('');
  const [offeringDropdownOpen, setOfferingDropdownOpen] = useState(false);
  const [autocompleteInputValues, setAutocompleteInputValues] = useState({});
  const [autocompleteOpen, setAutocompleteOpen] = useState({});
  const [expandedMultiAutocomplete, setExpandedMultiAutocomplete] = useState({});
  const [menuPlacement, setMenuPlacement] = useState({});
  const [dlDialogState, setDlDialogState] = useState({
    open: false,
    inputValue: '',
    errorMessage: '',
    duplicateMessage: '',
    pendingGroupId: '',
    isValidating: false,
  });
  const [jiraDialogState, setJiraDialogState] = useState({
    open: false,
    inputValue: '',
    errorMessage: '',
    isValidating: false,
  });

  // Loading states for individual API calls
  const [loadingStates, setLoadingStates] = useState({
    masterData: false,
    offering: false,
  });

  useEffect(() => {
    if (!isEditMode && !canCreateProject) {
      snackbarOpen('You do not have access to add projects', 'error');
      navigate('/delivery/project-overview', { replace: true });
    }
  }, [canCreateProject, isEditMode, navigate, snackbarOpen]);

  // Master Data States - ALL APIs COMBINED
  const [masterData, setMasterData] = useState({
    salesVertical: [],
    opportunity: [],
    deliveryMethodology: [],
    customerType: [],
    consumptionType: [],
    technology: [],
    offering: [],
    cloudProvider: [],
    region: [],
    country: [],
    sponsors: [],
    sponsorName: [],
    vertical: [],
    seniorManager: [],
    clientName: [],
    zohoClientName: [],
    costSplit: [],
    revenueSplit: [],
    billingType: [],
    fpBillingType: [],
    tmBillingType: [],
    billingEntity: [],
    masterProjectName: [],
    projectStatus: [],
  });

  const countryIdByNameRef = useRef({});
  const previousEditModeRef = useRef(isEditMode);

  // ==================== COMPUTED VALUES ====================

  const currentProjectType = useMemo(() => {
    return PROJECT_TYPE_OPTIONS.find((opt) => opt.value === selectedProjectType)?.type || PROJECT_TYPES.MASTER;
  }, [selectedProjectType]);

  const shouldShowInternalProjectCheckbox = currentProjectType === PROJECT_TYPES.TIME_AND_MATERIAL
    && (!isEditMode || canEditDetailProjectFields || !canEditManagerBasicProjectFields);

  useEffect(() => {
    if (isEditMode || currentProjectType === PROJECT_TYPES.MASTER) return;

    const defaultStatus = findOptionByIdOrName('In Progress', masterData.projectStatus)
      || masterData.projectStatus?.[0];
    if (!defaultStatus?.id) return;

    setFormData((prev) => (
      prev.projectStatus ? prev : { ...prev, projectStatus: String(defaultStatus.id) }
    ));
  }, [currentProjectType, isEditMode, masterData.projectStatus]);

  // Compute canSaveProject based on project type
  const canSaveProjectComputed = useMemo(() => {
    if (isEditMode) {
      return canUpdateProject && canEditAnyProjectFields;
    }
    // For master projects, use the more permissive check
    if (currentProjectType === PROJECT_TYPES.MASTER) {
      return canCreateMasterProject;
    }
    return canCreateProject;
  }, [
    isEditMode,
    canUpdateProject,
    canEditAnyProjectFields,
    currentProjectType,
    canCreateMasterProject,
    canCreateProject,
  ]);

  const projectTypeFromQuerySelection = useMemo(
    () => getProjectTypeFromSelection(selectedProjectTypeFromQuery),
    [selectedProjectTypeFromQuery],
  );
  const effectiveProjectTypeForBilling = useMemo(() => {
    if (isEditMode) {
      return lockedProjectType || projectTypeFromQuerySelection || currentProjectType;
    }
    return currentProjectType;
  }, [currentProjectType, isEditMode, lockedProjectType, projectTypeFromQuerySelection]);
  const billingTypeOptionsSource = useMemo(() => {
    const tmOptions = masterData?.tmBillingType || [];
    const fpOptions = masterData?.fpBillingType || [];
    const billingTypeValue = formData?.billingType;

    if (billingTypeValue !== undefined && billingTypeValue !== null && String(billingTypeValue).trim() !== '') {
      const tmMatch = findOptionByIdOrName(billingTypeValue, tmOptions);
      const fpMatch = findOptionByIdOrName(billingTypeValue, fpOptions);
      if (tmMatch && !fpMatch) return PROJECT_TYPES.TIME_AND_MATERIAL;
      if (fpMatch && !tmMatch) return PROJECT_TYPES.FIXED_PRICE;
    }

    return effectiveProjectTypeForBilling;
  }, [
    effectiveProjectTypeForBilling,
    formData?.billingType,
    masterData?.fpBillingType,
    masterData?.tmBillingType,
  ]);

  const visibleFields = useMemo(() => {
    return ProjectFields.filter((field) => {
      if (!field.visibleFor.includes(currentProjectType)) return false;
      if (field.name === 'internalProject') return false;
      if (field.name === 'projectStatus') return canEditManagerBasicProjectFields;
      if (!isEditMode) return true;
      if (BASIC_PROJECT_DETAIL_FIELDS.has(field.name)) return canEditManagerBasicProjectFields;
      return canEditDetailProjectFields;
    });
  }, [
    canEditDetailProjectFields,
    canEditManagerBasicProjectFields,
    currentProjectType,
    isEditMode,
  ]);

  const visibleFieldNames = useMemo(() => new Set(visibleFields.map((field) => field.name)), [visibleFields]);

  const requiredFieldsForType = useMemo(() => {
    const requiredFields = REQUIRED_FIELDS[currentProjectType] || [];
    if (!isEditMode) return requiredFields;
    return requiredFields.filter((fieldName) => visibleFieldNames.has(fieldName));
  }, [currentProjectType, isEditMode, visibleFieldNames]);

  /**
   * Determine if the "Investment" sponsor option is selected
   * (triggers sponsor partner/name sub-dropdown)
   */
  const isInvestmentSponsorSelected = useCallback((sponsorValue) => {
    return resolveIntegerId(sponsorValue, masterData?.sponsors) === INVESTMENT_SPONSOR_ID;
  }, [masterData?.sponsors]);

  // ==================== API CALLS ====================

  useEffect(() => {
    if (modeFromQuery === 'edit' && !projectIdFromQuery) {
      snackbarOpen('Project ID is required for edit mode', 'warning');
      navigate('/delivery/project-overview');
    }
  }, [modeFromQuery, navigate, projectIdFromQuery, snackbarOpen]);

  useEffect(() => {
    const wasEditMode = previousEditModeRef.current;
    if (wasEditMode && !isEditMode) {
      // Transitioned from update -> add; reset stale project state.
      setFormData({});
      setErrors({});
      setPrefillError('');
      setIsPrefillLoading(false);
      setLockedProjectType('');
      setAutocompleteInputValues({});
      setAutocompleteOpen({});
      setSelectedProjectType(selectedProjectTypeFromQuery || 'Add new Master project');
    }
    previousEditModeRef.current = isEditMode;
  }, [isEditMode, selectedProjectTypeFromQuery]);

  useEffect(() => {
    debugLog('[AddProject] Parsed query params', {
      projectId: projectIdFromQuery,
      mode: modeFromQuery,
      isEditable: isEditableFromQuery,
      variant: variantFromQuery,
      projectType: projectTypeFromQuery,
      isEditMode,
    });
  }, [isEditMode, isEditableFromQuery, modeFromQuery, projectIdFromQuery, projectTypeFromQuery, variantFromQuery]);

  useEffect(() => {
    fetchAllMasterData();
  }, []);

  useEffect(() => {
    if (!selectedProjectTypeFromQuery) return;
    if (isEditMode) return;
    setSelectedProjectType(selectedProjectTypeFromQuery);
  }, [isEditMode, selectedProjectTypeFromQuery]);

  /**
   * Fetch all master data from ALL APIs in parallel
   */
  const fetchAllMasterData = async () => {
    try {
      setLoadingStates((prev) => ({ ...prev, masterData: true }));
      setIsLoading(true);

      const [
        salesRes,
        oppRes,
        deliveryRes,
        revenueRes,
        customerRes,
        consumptionRes,
        techRes,
        regionsRes,
        sponsorsRes,
        verticalsRes,
        managersRes,
        clientsRes,
        zohoClientsRes,
        costSplitRes,
        fpBillingRes,
        tmBillingRes,
        billingEntityRes,
        masterProjectNameRes,
        projectStatusRes,
        cloudProviderRes,
        travelDestRes,
      ] = await Promise.allSettled([
        UseAxios(salesVerticalData()),
        UseAxios(opportunityData()),
        UseAxios(deliveryMethodologyData()),
        UseAxios(revenueSplitData()),
        UseAxios(customerTypeData()),
        UseAxios(consumptionTypeData()),
        UseAxios(technologyData()),
        UseAxios(getRegionsData()),
        UseAxios(getSponsorsData()),
        UseAxios(getProjectDetailsLegacyVerticalData()),
        UseAxios(seniorManagerData()),
        UseAxios(clientDetailsData()),
        UseAxios(zohoCustomerDetailsList()),
        UseAxios(costSplitData()),
        UseAxios(fpBillingTypeData()),
        UseAxios(tmBillingTypeData()),
        UseAxios(billingEntityData()),
        UseAxios(masterProjectNameData()),
        UseAxios(getProjectStatusMaster()),
        UseAxios(cloudProviderData()),
        UseAxios(getTravelDestination()),
      ]);

      // Process Sales Vertical
      const salesData = parseResponseData(getSettledResponseData(salesRes));
      const salesList = extractArrayFromResponse(salesData, ['salesVerticalDetails', 'data', 'result']);
      const normalizedSales = normalizeOptions(salesList, ['sales_vertical_name', 'name'], ['sales_vertical_id', 'id']);

      // Process Opportunity
      const oppData = parseResponseData(getSettledResponseData(oppRes));
      const oppList = extractArrayFromResponse(oppData, ['opportunityDetails', 'opportunities', 'data', 'result']);
      const normalizedOpp = normalizeOptions(oppList, ['opportunityName', 'opportunity_name', 'name'], ['opportunityId', 'opportunity_id', 'id']);

      // Process Delivery Methodology
      const deliveryData = parseResponseData(getSettledResponseData(deliveryRes));
      const deliveryList = extractArrayFromResponse(deliveryData, ['deliveryMethodologyList', 'deliveryMethodologyDetails', 'data', 'result']);
      const normalizedDelivery = normalizeOptions(deliveryList, ['methodology', 'delivery_methodology_name', 'name'], ['methodId', 'delivery_methodology_id', 'id']);

      // Process Revenue Split
      const revenueData = parseResponseData(getSettledResponseData(revenueRes));
      const revenueList = extractArrayFromResponse(revenueData, ['revenueSplitDetails', 'data', 'result']);
      const normalizedRevenue = normalizeOptions(revenueList, ['revenue_split_name', 'revenue_split', 'name'], ['revenue_split_id', 'id']);

      // Process Customer Type
      const customerData = parseResponseData(getSettledResponseData(customerRes));
      const customerList = extractArrayFromResponse(customerData, ['projectCustomerTypeDetails', 'data', 'result']);
      const normalizedCustomer = normalizeOptions(customerList, ['customer_type_name', 'customer_type', 'name'], ['customer_type_id', 'id']);

      // Process Consumption Type
      const consumptionData = parseResponseData(getSettledResponseData(consumptionRes));
      const consumptionList = extractArrayFromResponse(consumptionData, ['consumptionTypeList', 'consumptionTypeDetails', 'data', 'result']);
      const normalizedConsumption = normalizeOptions(consumptionList, ['consumptionName', 'consumption_type', 'name'], ['consumptionId', 'consumption_type_id', 'id']);

      // Process Technology
      const technologyResponse = parseResponseData(getSettledResponseData(techRes));
      const technologyList = extractArrayFromResponse(technologyResponse, ['technologyList', 'technologyDetails', 'technologies', 'data', 'result']);
      const normalizedTechnology = normalizeOptions(
        technologyList,
        ['technology', 'technologyName', 'technology_name', 'name'],
        ['technologyId', 'techId', 'tech_id', 'id'],
      );

      // Process Regions
      const regionsData = parseResponseData(getSettledResponseData(regionsRes));
      const regionsList = extractArrayFromResponse(regionsData, ['regionDetails', 'regions', 'data', 'result']);
      const normalizedRegions = normalizeOptions(
        regionsList,
        ['regionName', 'name', 'label'],
        ['region_id', 'regionId', 'id', 'value'],
      );

      // Process Sponsors
      const sponsorsData = parseResponseData(getSettledResponseData(sponsorsRes));
      const sponsorsList = extractArrayFromResponse(sponsorsData, ['sponsorDetails', 'sponsors', 'data', 'result']);
      const normalizedSponsors = normalizeOptions(sponsorsList, ['sponsorName', 'sponsor_name', 'name', 'label'], ['sponsorId', 'sponsor_id', 'id', 'value']);

      // Process Verticals
      const verticalsData = parseResponseData(getSettledResponseData(verticalsRes));
      const verticalsList = extractArrayFromResponse(verticalsData, ['verticalDetails', 'verticals', 'data', 'result']);
      const normalizedVerticals = normalizeOptions(verticalsList, ['verticalName', 'vertical_name', 'name', 'label'], ['id', 'verticalId', 'vertical_id', 'value']);

      // Process Senior Managers
      const managersData = parseResponseData(getSettledResponseData(managersRes));
      const managersList = extractArrayFromResponse(managersData, ['seniorManagers', 'managers', 'users', 'data', 'result']);
      const normalizedManagers = (Array.isArray(managersList) ? managersList : [])
        .map((item) => {
          if (!item || typeof item !== 'object') return null;

          const empId = extractValue(item, ['emp_id', 'employee_id', 'employeeId', 'empId']);
          const empNo = extractValue(item, ['emp_no', 'id', 'value']);
          const firstName = extractValue(item, ['first_name', 'firstName']);
          const lastName = extractValue(item, ['last_name', 'lastName']);
          const fullName = String(
            extractValue(item, ['full_name', 'fullName', 'name', 'employeeName', 'userName', 'label'])
            || [firstName, lastName].filter(Boolean)
              .join(' ')
              .trim()
            || empId
            || empNo
            || '',
          ).trim();

          if (!fullName) return null;

          return {
            id: String(empId || fullName),
            empId: String(empId || ''),
            emp_no: empNo,
            numericId: toPositiveInteger(empNo),
            name: String(fullName),
          };
        })
        .filter(Boolean)
        .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' }));
      const dedupedManagers = dedupeManagerOptions(normalizedManagers);

      // Process Clients
      const clientsData = parseResponseData(getSettledResponseData(clientsRes));
      const clientsList = extractArrayFromResponse(clientsData, ['clientDetails', 'clients', 'data', 'result']);
      const normalizedClients = normalizeOptions(clientsList, ['clientName', 'client_name', 'name', 'label'], ['clientId', 'client_id', 'id', 'value']);

      // Process Zoho Clients
      const zohoClientsData = parseResponseData(getSettledResponseData(zohoClientsRes));
      const zohoClientsList = extractArrayFromResponse(
        zohoClientsData,
        ['zohoCustomerDetails', 'zohoCustomers', 'customers', 'data', 'result'],
      );
      const normalizedZohoClients = normalizeOptions(
        zohoClientsList,
        ['zohoCustomerName', 'zoho_customer_name', 'customerName', 'name', 'label'],
        ['zohoCustomerId', 'zoho_customer_id', 'zohoClientId', 'zoho_client_id', 'id', 'value'],
      );

      // Process Cost Split
      const costSplitData2 = parseResponseData(getSettledResponseData(costSplitRes));
      const costSplitList = extractArrayFromResponse(costSplitData2, ['costSplitDetails', 'costSplits', 'data', 'result']);
      const normalizedCostSplit = normalizeOptions(costSplitList, ['costSplitName', 'cost_split_name', 'name', 'label'], ['costSplitId', 'cost_split_id', 'id', 'value']);

      // Process FP Billing Types
      const fpBillingData = parseResponseData(getSettledResponseData(fpBillingRes));
      const fpBillingList = extractArrayFromResponse(fpBillingData, ['fpBillingTypes', 'billingTypes', 'data', 'result']);
      const normalizedFpBilling = normalizeOptions(fpBillingList, ['billingTypeName', 'billing_type_name', 'name', 'label'], ['billingTypeId', 'billing_type_id', 'id', 'value']);

      // Process TM Billing Types
      const tmBillingData = parseResponseData(getSettledResponseData(tmBillingRes));
      const tmBillingList = extractArrayFromResponse(tmBillingData, ['tmBillingTypes', 'billingTypes', 'data', 'result']);
      const normalizedTmBilling = normalizeOptions(tmBillingList, ['billingTypeName', 'billing_type_name', 'name', 'label'], ['billingTypeId', 'billing_type_id', 'id', 'value']);

      // Process Billing Entities
      const billingEntityData2 = parseResponseData(getSettledResponseData(billingEntityRes));
      const billingEntityList = extractArrayFromResponse(
        billingEntityData2,
        ['billingEntities', 'billingEntityDetails', 'billing_entities', 'billing_entity_details', 'data', 'result'],
      );
      const normalizedBillingEntity = normalizeOptions(
        billingEntityList,
        ['billingEntityName', 'billingEntity', 'billing_entity', 'billing_entity_name', 'entityName', 'entity_name', 'name', 'label'],
        ['billingEntityId', 'billing_entity_id', 'id', 'value'],
      );

      // Process Master Project Names
      const masterProjectNameData2 = parseResponseData(getSettledResponseData(masterProjectNameRes));
      if (
        masterProjectNameData2?.name === 'AxiosError'
        || masterProjectNameData2?.isAxiosError
        || masterProjectNameData2?.success === false
      ) {
        const serverMessage = getServerMessage(masterProjectNameData2, 'Failed to fetch project list');
        snackbarOpen(`Failed to fetch project list: ${serverMessage}`, 'error');
      }
      const masterProjectNameList = extractArrayFromResponse(
        masterProjectNameData2,
        ['rows', 'masterProjectNameDetails', 'masterProjectNames', 'projectNames', 'data', 'result'],
      );
      const normalizedMasterProjectNames = (Array.isArray(masterProjectNameList) ? masterProjectNameList : [])
        .map((item) => {
          const name
            = extractValue(item, ['masterProjectName', 'master_project_name', 'projectName', 'project_name', 'name', 'label'])
            || item?.project?.projectName
            || item?.project?.masterProjectName
            || '';
          const id
            = extractValue(item, ['masterProjectId', 'master_project_id', 'projectId', 'project_id', 'id', 'value'])
            || item?.project?.projectId
            || item?.project?.project_id
            || item?.project?.projectSeqId
            || item?.project?.project_seq_id
            || '';
          if (!name || !id) return null;
          return { id: String(id), name: String(name) };
        })
        .filter(Boolean);

      const projectStatusData = parseResponseData(getSettledResponseData(projectStatusRes));
      const projectStatusList = extractArrayFromResponse(
        projectStatusData,
        ['projectStatuses', 'projectStatusDetails', 'statuses', 'data', 'result'],
      );
      const normalizedProjectStatuses = normalizeOptions(
        projectStatusList,
        ['project_status_name', 'projectStatusName', 'statusName', 'name', 'label'],
        ['project_status_id', 'projectStatusId', 'statusId', 'id', 'value'],
      );

      const cloudProviderResponse = parseResponseData(getSettledResponseData(cloudProviderRes));
      const cloudProviderList = extractArrayFromResponse(
        cloudProviderResponse,
        ['cloudProviders', 'cloudProviderDetails', 'data', 'result'],
      );
      const normalizedCloudProviders = normalizeOptions(
        cloudProviderList.filter(isActiveCloudProvider),
        ['cloud_provider_name', 'name', 'label'],
        ['cloud_provider_id', 'id', 'value'],
      );

      // Build country name -> country id lookup from travel destination API
      const travelDestData = parseResponseData(getSettledResponseData(travelDestRes));
      const travelDestList = extractArrayFromResponse(
        travelDestData,
        ['cities', 'cityDetails', 'data', 'result'],
      );
      const travelCountries = normalizeOptions(
        travelDestList,
        ['country_name', 'countryName', 'name', 'label'],
        ['country_id', 'countryId', 'id', 'value'],
      );
      countryIdByNameRef.current = travelCountries.reduce((acc, item) => {
        const key = String(item?.name || '').trim()
          .toLowerCase();
        const numericId = toInteger(item?.id);
        if (key && numericId !== null) {
          acc[key] = numericId;
        }
        return acc;
      }, {});

      setMasterData((prev) => ({
        salesVertical: normalizedSales,
        opportunity: normalizedOpp,
        deliveryMethodology: normalizedDelivery,
        customerType: normalizedCustomer,
        consumptionType: normalizedConsumption,
        technology: normalizedTechnology,
        offering: Array.isArray(prev?.offering) ? prev.offering : [],
        cloudProvider: normalizedCloudProviders,
        region: normalizedRegions,
        country: Array.isArray(prev?.country) ? prev.country : [],
        sponsors: normalizedSponsors,
        sponsorName: Array.isArray(prev?.sponsorName) ? prev.sponsorName : [],
        vertical: normalizedVerticals,
        seniorManager: dedupedManagers,
        clientName: normalizedClients,
        zohoClientName: normalizedZohoClients,
        costSplit: normalizedCostSplit,
        revenueSplit: normalizedRevenue,
        billingType: currentProjectType === PROJECT_TYPES.FIXED_PRICE ? normalizedFpBilling : normalizedTmBilling,
        fpBillingType: normalizedFpBilling,
        tmBillingType: normalizedTmBilling,
        billingEntity: normalizedBillingEntity,
        masterProjectName: normalizedMasterProjectNames,
        projectStatus: normalizedProjectStatuses,
      }));
    } catch (error) {
      console.error('Error fetching master data:', error);
      snackbarOpen(getServerMessage(error, 'Failed to load master data'), 'error');
    } finally {
      setLoadingStates((prev) => ({ ...prev, masterData: false }));
      setIsLoading(false);
    }
  };

  const fetchProjectDetailsForEdit = useCallback(async () => {
    if (!isEditMode || !projectIdFromQuery || !userProfile) return;

    try {
      setPrefillError('');
      setIsPrefillLoading(true);
      const response = await UseAxios(fetchAddProjectPrefill({
        projectId: projectIdFromQuery,
        projectSeqId: projectSeqIdFromQuery,
      }));
      const data = parseResponseData(response?.data);
      if (data?.name === 'AxiosError' || data?.isAxiosError) {
        throw new Error(getServerMessage(data, 'Failed to load project details'));
      }
      const projectData = getPrefillProjectData(data);
      if (!projectData) {
        snackbarOpen('Project details not found for edit', 'error');
        setPrefillError('Project details not found for edit');
        return;
      }

      debugLog('[AddProject] Prefill response keys', Object.keys(projectData || {}));

      const projectTypeFromId = getProjectTypeById(
        getFirstValueByPaths(projectData, [
          'project_type_id',
          'projectTypeId',
          'project.project_type_id',
          'project.projectTypeId',
        ]),
      );
      const resolvedProjectType = projectTypeFromId === PROJECT_TYPES.MASTER
        ? resolveProjectTypeFromData(projectData)
        : projectTypeFromId;
      const queryProjectType = getProjectTypeFromSelection(selectedProjectTypeFromQuery);
      const projectType = resolvedProjectType === PROJECT_TYPES.MASTER
        ? (queryProjectType || PROJECT_TYPES.MASTER)
        : resolvedProjectType;
      const projectTypeOption = PROJECT_TYPE_OPTIONS.find((option) => option.type === projectType);
      // In edit mode, persisted project type should win over URL/query defaults.
      setSelectedProjectType(projectTypeOption?.value || 'Add new Master project');
      if (isEditMode) {
        setLockedProjectType(projectType);
      }

      const nextFormData = mapProjectDetailsToFormData(projectData);
      const resolvedProjectId = projectIdFromQuery;

      if (resolvedProjectId) {
        const projectTechnologyResponse = await UseAxios(
          fetchProjectTechnologyList({ projectId: resolvedProjectId }),
        );
        const projectTechnologyData = parseResponseData(projectTechnologyResponse?.data);

        if (
          !(projectTechnologyData?.name === 'AxiosError' || projectTechnologyData?.isAxiosError)
          || isNoDataResponse(projectTechnologyData)
        ) {
          const latestProjectTechnology = getProjectTechnologyRows(projectTechnologyData)[0] || null;
          const latestTechId = getFirstValueByPaths(latestProjectTechnology, ['techId', 'tech_id']);

          if (latestTechId !== undefined && latestTechId !== null && latestTechId !== '') {
            nextFormData.technology = String(latestTechId);
          }
        }
      }

      const prefetchedTechnologyOption = findOptionByIdOrName(nextFormData.technology, masterData.technology);
      if (prefetchedTechnologyOption?.name) {
        setAutocompleteInputValues((prev) => ({
          ...prev,
          technology: String(prefetchedTechnologyOption.name),
        }));
      }

      setFormData(nextFormData);
      setErrors({});
    } catch (error) {
      console.error('Error loading project details for edit:', error);
      const message = getServerMessage(error, 'Failed to load project details');
      setPrefillError(message);
      snackbarOpen(message, 'error');
    } finally {
      setIsPrefillLoading(false);
    }
  }, [isEditMode, projectIdFromQuery, projectSeqIdFromQuery, selectedProjectTypeFromQuery, snackbarOpen, userProfile]);

  useEffect(() => {
    fetchProjectDetailsForEdit();
  }, [fetchProjectDetailsForEdit]);

  useEffect(() => {
    if (!isEditMode) return;
    if (currentProjectType === PROJECT_TYPES.MASTER) return;

    setFormData((prev) => {
      if (!prev || typeof prev !== 'object') return prev;

      const normalizedDeliveryManager = findOptionByIdOrName(prev.deliveryManager, masterData.seniorManager);
      const normalizedSeniorManager = findOptionByIdOrName(prev.seniorManager, masterData.seniorManager);
      const normalizedMasterProject = findOptionByIdOrName(prev.masterProjectName, masterData.masterProjectName);
      const normalizedTechnology = normalizeSingleSelectValue(prev.technology, masterData.technology);
      const normalizedZohoClient = normalizeSingleSelectValue(prev.zohoClientName, masterData.zohoClientName);
      const normalizedVertical = normalizeSingleSelectValue(prev.vertical, masterData.vertical);
      const normalizedCountry = normalizeSingleSelectValue(prev.country, masterData.country);
      const normalizedDeliveryMethodology = normalizeSingleSelectValue(prev.deliveryMethodology, masterData.deliveryMethodology);
      const normalizedSponsor = normalizeSingleSelectValue(prev.sponsors, masterData.sponsors);
      const normalizedSponsorName = normalizeSingleSelectValue(prev.sponsorName, masterData.sponsorName);
      const normalizedOfferings = normalizeMultiSelectValues(prev.offering, masterData.offering);
      const normalizedCloudProviders = normalizeMultiSelectValues(prev.cloudProvider, masterData.cloudProvider);
      const normalizedArchitects = normalizeMultiSelectValues(prev.architect, masterData.seniorManager);
      const normalizedSeniorArchitects = normalizeMultiSelectValues(prev.seniorArchitect, masterData.seniorManager);

      let hasChanges = false;
      const next = { ...prev };

      if (
        normalizedDeliveryManager
        && String(prev.deliveryManager || '').trim() !== String(normalizedDeliveryManager.id || '').trim()
      ) {
        next.deliveryManager = String(normalizedDeliveryManager.id);
        hasChanges = true;
      }

      if (
        normalizedSeniorManager
        && String(prev.seniorManager || '').trim() !== String(normalizedSeniorManager.id || '').trim()
      ) {
        next.seniorManager = String(normalizedSeniorManager.id);
        hasChanges = true;
      }

      if (
        normalizedMasterProject
        && String(prev.masterProjectName || '').trim() !== String(normalizedMasterProject.id || '').trim()
      ) {
        next.masterProjectName = String(normalizedMasterProject.id);
        hasChanges = true;
      }

      if (
        normalizedTechnology
        && String(prev.technology || '').trim() !== normalizedTechnology
      ) {
        next.technology = normalizedTechnology;
        hasChanges = true;
      }

      if (normalizedTechnology) {
        const technologyOption = findOptionByIdOrName(normalizedTechnology, masterData.technology);
        if (technologyOption?.name && autocompleteInputValues.technology !== technologyOption.name) {
          setAutocompleteInputValues((existing) => ({
            ...existing,
            technology: String(technologyOption.name),
          }));
        }
      }

      if (
        normalizedZohoClient
        && String(prev.zohoClientName || '').trim() !== normalizedZohoClient
      ) {
        next.zohoClientName = normalizedZohoClient;
        hasChanges = true;
      }

      if (
        normalizedVertical
        && String(prev.vertical || '').trim() !== normalizedVertical
      ) {
        next.vertical = normalizedVertical;
        hasChanges = true;
      }

      if (
        normalizedCountry
        && String(prev.country || '').trim() !== normalizedCountry
      ) {
        next.country = normalizedCountry;
        hasChanges = true;
      }

      if (
        normalizedDeliveryMethodology
        && String(prev.deliveryMethodology || '').trim() !== normalizedDeliveryMethodology
      ) {
        next.deliveryMethodology = normalizedDeliveryMethodology;
        hasChanges = true;
      }

      if (
        normalizedSponsor
        && String(prev.sponsors || '').trim() !== normalizedSponsor
      ) {
        next.sponsors = normalizedSponsor;
        hasChanges = true;
      }

      if (
        normalizedSponsorName
        && String(prev.sponsorName || '').trim() !== normalizedSponsorName
      ) {
        next.sponsorName = normalizedSponsorName;
        hasChanges = true;
      }

      if (
        normalizedOfferings.length > 0
        && JSON.stringify((Array.isArray(prev.offering) ? prev.offering : []).map(String).sort())
          !== JSON.stringify(normalizedOfferings.slice().sort())
      ) {
        next.offering = normalizedOfferings;
        hasChanges = true;
      }

      if (
        JSON.stringify((Array.isArray(prev.cloudProvider) ? prev.cloudProvider : []).map(String).sort())
          !== JSON.stringify(normalizedCloudProviders.slice().sort())
      ) {
        next.cloudProvider = normalizedCloudProviders;
        hasChanges = true;
      }

      if (
        JSON.stringify((Array.isArray(prev.architect) ? prev.architect : []).map(String).sort())
          !== JSON.stringify(normalizedArchitects.slice().sort())
      ) {
        next.architect = normalizedArchitects;
        hasChanges = true;
      }

      if (
        JSON.stringify((Array.isArray(prev.seniorArchitect) ? prev.seniorArchitect : []).map(String).sort())
          !== JSON.stringify(normalizedSeniorArchitects.slice().sort())
      ) {
        next.seniorArchitect = normalizedSeniorArchitects;
        hasChanges = true;
      }

      return hasChanges ? next : prev;
    });
  }, [
    currentProjectType,
    isEditMode,
    masterData.country,
    masterData.deliveryMethodology,
    masterData.masterProjectName,
    masterData.offering,
    masterData.seniorManager,
    masterData.sponsorName,
    masterData.sponsors,
    masterData.technology,
    masterData.zohoClientName,
    masterData.vertical,
  ]);

  /**
   * Fetch offerings based on selected vertical (driven by formData.vertical)
   */
  const fetchOfferingsByVertical = useCallback(async (verticalId) => {
    if (!hasSelectedValue(verticalId)) {
      setLoadingStates((prev) => ({ ...prev, offering: false }));
      setMasterData((prev) => ({ ...prev, offering: [] }));
      return;
    }

    try {
      setLoadingStates((prev) => ({ ...prev, offering: true }));
      const response = await UseAxios(projectOfferingData(encodeURIComponent(String(verticalId))));
      const data = parseResponseData(response.data);

      const offeringList = extractArrayFromResponse(data, ['projectOfferings', 'projectOfferingDetails', 'offerings', 'data', 'result']);

      const normalizedOfferings = normalizeOptions(
        offeringList,
        ['offering_name', 'projectOfferingName', 'project_offering_name', 'offeringName', 'offering', 'name'],
        ['project_offering_id', 'projectOfferingId', 'project_offering_id', 'offering_id', 'offeringId', 'id'],
      );

      setMasterData((prev) => ({ ...prev, offering: dedupeOptions(normalizedOfferings) }));
    } catch (error) {
      console.error('Error fetching offerings:', error);
      snackbarOpen('Failed to load offerings', 'error');
      setMasterData((prev) => ({ ...prev, offering: [] }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, offering: false }));
    }
  }, [snackbarOpen]);

  // Re-fetch offerings whenever the vertical field changes
  useEffect(() => {
    const resolvedVerticalId = resolveIntegerId(formData.vertical, masterData.vertical);
    fetchOfferingsByVertical(resolvedVerticalId);
  }, [fetchOfferingsByVertical, formData.vertical, masterData.vertical]);

  /**
   * Fetch countries based on selected region
   */
  useEffect(() => {
    let isActive = true;

    const regionValue = formData.region;
    if (!regionValue) {
      setMasterData((prev) => ({ ...prev, country: [] }));
      return () => {
        isActive = false;
      };
    }

    const loadCountries = async () => {
      try {
        const resolvedRegionId = resolveIntegerId(regionValue, masterData?.region || []);
        const numericRegionId = Number(resolvedRegionId ?? regionValue);
        const regionId = Number.isNaN(numericRegionId)
          ? (resolvedRegionId ?? regionValue)
          : numericRegionId;
        const response = await UseAxios(regionCountryData(regionId));
        const data = parseResponseData(response?.data);
        const list = extractArrayFromResponse(data, ['countryDetails', 'countries', 'data', 'result']);
        const options = (Array.isArray(list) ? list : [])
          .map((item) => {
            const countryName = typeof item === 'string'
              ? item
              : extractValue(item, ['countryName', 'country_name', 'name', 'label']);
            if (!countryName) return null;

            const rawCountryId = typeof item === 'string'
              ? null
              : extractValue(item, ['country_id', 'countryId', 'id', 'value']);
            const directCountryId = toPositiveInteger(rawCountryId);
            const lookupCountryId = countryIdByNameRef.current[String(countryName).trim()
              .toLowerCase()] ?? null;
            const finalId = directCountryId ?? lookupCountryId ?? countryName;

            return {
              id: String(finalId),
              name: String(countryName),
            };
          })
          .filter(Boolean);
        if (isActive) setMasterData((prev) => ({ ...prev, country: options }));
      } catch {
        if (isActive) setMasterData((prev) => ({ ...prev, country: [] }));
      }
    };

    loadCountries();

    return () => {
      isActive = false;
    };
  }, [formData.region, masterData?.region]);

  /**
   * Fetch sponsor partners when the "Investment" sponsor option is selected
   */
  useEffect(() => {
    let isActive = true;

    const sponsorValue = formData.sponsors;
    const matchedSponsor = (masterData?.sponsors || []).find((option) => {
      const optionId = String(option?.id || '').trim()
        .toLowerCase();
      const optionName = String(option?.name || '').trim()
        .toLowerCase();
      const selected = String(sponsorValue || '').trim()
        .toLowerCase();
      return optionId === selected || optionName === selected;
    });
    const sponsorId = Number(matchedSponsor?.id ?? sponsorValue);

    if (!isInvestmentSponsorSelected(sponsorValue) || Number.isNaN(sponsorId)) {
      setMasterData((prev) => ({ ...prev, sponsorName: [] }));
      return () => {
        isActive = false;
      };
    }

    const loadPartners = async () => {
      try {
        const response = await UseAxios(getSponsorPartnersData(sponsorId));
        const data = parseResponseData(response?.data);
        const list = extractArrayFromResponse(data, ['partnerDetails', 'partners', 'data', 'result']);
        const options = normalizeOptions(list, ['partnerName', 'partner_name', 'name', 'label'], ['partnerId', 'partner_id', 'id', 'value']);
        if (isActive) setMasterData((prev) => ({ ...prev, sponsorName: options }));
      } catch {
        if (isActive) setMasterData((prev) => ({ ...prev, sponsorName: [] }));
      }
    };

    loadPartners();

    return () => {
      isActive = false;
    };
  }, [formData.sponsors, isInvestmentSponsorSelected, masterData?.sponsors]);

  // ==================== EVENT HANDLERS ====================

  /**
   * Handle input field changes
   */
  const handleInputChange = useCallback((fieldName, value) => {
    const numericOnlyFields = new Set(['noOfResources', 'billingId', 'redmineId', 'hubspotId']);
    const sanitizedValue = numericOnlyFields.has(fieldName)
      ? String(value ?? '').replace(/\D/g, '')
      : value;

    setFormData((prev) => {
      const next = { ...prev, [fieldName]: sanitizedValue };

      // Any region change invalidates previously selected country
      if (fieldName === 'region' && sanitizedValue !== prev.region) {
        next.country = '';
      }

      // Any vertical change invalidates previously selected offerings
      if (fieldName === 'vertical' && sanitizedValue !== prev.vertical) {
        next.offering = [];
      }

      // When sponsors changes away from investment, clear sponsorName
      if (fieldName === 'sponsors' && !isInvestmentSponsorSelected(sanitizedValue)) {
        next.sponsorName = '';
      }

      return next;
    });

    // Clear error for this field
    setErrors((prev) => {
      const nextErrors = { ...prev, [fieldName]: '' };
      if (fieldName === 'region') {
        nextErrors.country = '';
      }
      if (fieldName === 'vertical') {
        nextErrors.offering = '';
      }
      if (fieldName === 'sponsors') {
        nextErrors.sponsorName = '';
      }
      return nextErrors;
    });
  }, [isInvestmentSponsorSelected]);

  const openDlDialog = useCallback(() => {
    setDlDialogState({
      open: true,
      inputValue: String(formData.dlGroupId || ''),
      errorMessage: '',
      duplicateMessage: '',
      pendingGroupId: '',
      isValidating: false,
    });
  }, [formData.dlGroupId]);

  const closeDlDialog = useCallback(() => {
    setDlDialogState((prev) => ({
      ...prev,
      open: false,
      errorMessage: '',
      duplicateMessage: '',
      pendingGroupId: '',
      isValidating: false,
    }));
  }, []);

  const acceptDlGroupId = useCallback((groupId) => {
    handleInputChange('dlGroupId', groupId);
    setDlDialogState({
      open: false,
      inputValue: '',
      errorMessage: '',
      duplicateMessage: '',
      pendingGroupId: '',
      isValidating: false,
    });
  }, [handleInputChange]);

  const handleDlDialogInputChange = useCallback((event) => {
    setDlDialogState((prev) => ({
      ...prev,
      inputValue: event.target.value,
      errorMessage: '',
      duplicateMessage: '',
      pendingGroupId: '',
    }));
  }, []);

  const handleDlValidation = useCallback(async () => {
    const groupId = String(dlDialogState.inputValue || '').trim();

    if (!groupId) {
      acceptDlGroupId('');
      return;
    }

    setDlDialogState((prev) => ({ ...prev, isValidating: true, errorMessage: '', duplicateMessage: '' }));

    try {
      const response = await UseAxios(validateDlGroupId(groupId));
      const data = parseResponseData(response?.data);

      if (!data?.valid) {
        setDlDialogState((prev) => ({
          ...prev,
          isValidating: false,
          errorMessage: data?.message || 'Unable to validate DL Id.',
          pendingGroupId: '',
        }));
        return;
      }

      const validatedGroupId = data.groupId || groupId;
      if (data.requiresConfirmation && !String(formData.masterProjectName || '').trim()) {
        snackbarOpen(data.message || 'This DL id already exists in another project.', 'warning');
      }

      acceptDlGroupId(validatedGroupId);
    } catch (error) {
      setDlDialogState((prev) => ({
        ...prev,
        isValidating: false,
        errorMessage: getServerMessage(error, 'Unable to validate DL Id.'),
        pendingGroupId: '',
      }));
    }
  }, [acceptDlGroupId, dlDialogState.inputValue, formData.masterProjectName, snackbarOpen]);

  const confirmDuplicateDlGroupId = useCallback(() => {
    acceptDlGroupId(dlDialogState.pendingGroupId || dlDialogState.inputValue);
  }, [acceptDlGroupId, dlDialogState.inputValue, dlDialogState.pendingGroupId]);

  const openJiraDialog = useCallback(() => {
    setJiraDialogState({
      open: true,
      inputValue: formData.jiraProjectId || '',
      errorMessage: '',
      isValidating: false,
    });
  }, [formData.jiraProjectId]);

  const closeJiraDialog = useCallback(() => {
    setJiraDialogState((prev) => ({
      ...prev,
      open: false,
      errorMessage: '',
      isValidating: false,
    }));
  }, []);

  const acceptJiraProjectId = useCallback((projectId) => {
    handleInputChange('jiraProjectId', projectId);
    setJiraDialogState({
      open: false,
      inputValue: '',
      errorMessage: '',
      isValidating: false,
    });
  }, [handleInputChange]);

  const handleJiraDialogInputChange = useCallback((event) => {
    setJiraDialogState((prev) => ({
      ...prev,
      inputValue: event.target.value,
      errorMessage: '',
    }));
  }, []);

  const handleJiraValidation = useCallback(async () => {
    const projectId = String(jiraDialogState.inputValue || '').trim();

    if (!projectId) {
      acceptJiraProjectId('');
      return;
    }

    setJiraDialogState((prev) => ({ ...prev, isValidating: true, errorMessage: '' }));

    try {
      const response = await UseAxios(validateJiraProjectId(projectId));
      const data = parseResponseData(response?.data);

      if (!data?.valid) {
        setJiraDialogState((prev) => ({
          ...prev,
          isValidating: false,
          errorMessage: data?.message || 'Unable to validate JIRA Project Id.',
        }));
        return;
      }

      if (data.isDuplicate && !String(formData.masterProjectName || '').trim()) {
        snackbarOpen(
          data.warningMessage || data.message || 'Duplicate JIRA Project ID already exists',
          'warning',
        );
      }

      acceptJiraProjectId(data.jiraProjectId || projectId);
    } catch (error) {
      setJiraDialogState((prev) => ({
        ...prev,
        isValidating: false,
        errorMessage: getServerMessage(error, 'Unable to validate JIRA Project Id.'),
      }));
    }
  }, [acceptJiraProjectId, jiraDialogState.inputValue, formData.masterProjectName, snackbarOpen]);

  /**
   * Handle project type dropdown change
   */
  const handleProjectTypeChange = useCallback((value) => {
    if (isEditMode) return;
    const selectedValue = value?.target?.value?.value || value?.value || value;
    setSelectedProjectType(selectedValue);
    setFormData({});
    setErrors({});
    setAutocompleteInputValues({});
    setAutocompleteOpen({});
  }, [isEditMode]);

  const handleBackNavigation = useCallback(() => {
    if (isEditMode && projectIdFromQuery) {
      navigate(`/delivery/project-details?projectId=${encodeURIComponent(String(projectIdFromQuery))}`);
      return;
    }
    navigate('/delivery/project-overview');
  }, [isEditMode, navigate, projectIdFromQuery]);

  const estimateShouldOpenUp = useCallback((event) => {
    const target = event?.currentTarget;
    if (!target) return false;
    const rect = target.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const menuMaxHeight = 5 * 33 + 16;
    const spaceBelow = viewportHeight - rect.bottom;
    return spaceBelow < menuMaxHeight + 24;
  }, []);

  const getMenuProps = useCallback(
    (fieldName) => {
      const openUp = menuPlacement[fieldName] === 'up';
      return {
        disablePortal: true,
        PaperProps: { className: 'add-project-menu' },
        anchorOrigin: { vertical: openUp ? 'top' : 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: openUp ? 'bottom' : 'top', horizontal: 'left' },
      };
    },
    [menuPlacement],
  );

  const handleMenuOpen = useCallback(
    (fieldName) => (event) => {
      const shouldOpenUp = estimateShouldOpenUp(event);
      setMenuPlacement((prev) => ({ ...prev, [fieldName]: shouldOpenUp ? 'up' : 'down' }));
    },
    [estimateShouldOpenUp],
  );

  /**
   * Validate form before submission
   */
  const validateForm = useCallback(() => {
    const validationErrors = {};

    requiredFieldsForType.forEach((fieldName) => {
      const value = formData[fieldName];
      if (!value || (Array.isArray(value) && value.length === 0)) {
        validationErrors[fieldName] = 'required';
      }
    });

    // Conditional validation: country required if region selected
    if (formData.region && !formData.country) {
      validationErrors.country = 'required';
    }

    // Conditional validation: sponsorName required only for investment sponsor
    if (isInvestmentSponsorSelected(formData.sponsors) && !formData.sponsorName) {
      validationErrors.sponsorName = 'required';
    }

    // Sales GM% supports PHP-compatible NA / '-' plus numeric values.
    const salesGmRaw = String(formData.salesGMPercent ?? '').trim();
    if (salesGmRaw) {
      const isSpecialSalesGm = salesGmRaw === '-' || salesGmRaw.toUpperCase() === 'NA';
      if (!isSpecialSalesGm) {
        const parsedSalesGm = Number(salesGmRaw.replace(/[,%\s]/g, ''));
        const isInvalidSalesGm
          = !Number.isFinite(parsedSalesGm)
          || parsedSalesGm < SALES_GM_MIN
          || parsedSalesGm > SALES_GM_MAX;
        if (isInvalidSalesGm) {
          validationErrors.salesGMPercent = 'invalidSalesGMPercent';
        }
      }
    }

    const noOfResourcesValue = String(formData.noOfResources ?? '').trim();
    if (noOfResourcesValue && !/^\d+$/.test(noOfResourcesValue)) {
      validationErrors.noOfResources = 'invalidNoOfResources';
    }

    const hubspotId = String(formData.hubspotId ?? '').trim();
    if (hubspotId && (!/^\d+$/.test(hubspotId) || hubspotId.length > 30)) {
      validationErrors.hubspotId = 'invalidHubspotId';
    }

    const redmineId = String(formData.redmineId ?? '').trim();
    if (redmineId) {
      const parsedRedmineId = Number(redmineId);
      if (!/^\d+$/.test(redmineId) || !Number.isFinite(parsedRedmineId) || parsedRedmineId > REDMINE_ID_MAX) {
        validationErrors.redmineId = 'invalidRedmineId';
      }
    }

    return validationErrors;
  }, [formData, isInvestmentSponsorSelected, requiredFieldsForType]);

  const handleValidation = useCallback(() => {
    if (!userProfile) {
      snackbarOpen('User profile not found', 'error');
      return false;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length === 0) {
      return true;
    }

    setErrors(validationErrors);
    if (validationErrors.salesGMPercent === 'invalidSalesGMPercent') {
      snackbarOpen(`Sales GM% must be "-", NA, or between ${SALES_GM_MIN} and ${SALES_GM_MAX}`, 'error');
      return false;
    }
    if (validationErrors.hubspotId === 'invalidHubspotId') {
      snackbarOpen('Hubspot ID must be numeric and at most 30 digits', 'error');
      return false;
    }
    if (validationErrors.redmineId === 'invalidRedmineId') {
      snackbarOpen(`Redmine ID must be numeric and at most ${REDMINE_ID_MAX}`, 'error');
      return false;
    }

    snackbarOpen('Please fill all mandatory fields', 'error');
    return false;
  }, [snackbarOpen, userProfile, validateForm]);

  const handleMasterSave = useCallback(async () => {
    const masterProjectName = String(formData.masterProjectName || '').trim();
    const masterPayload = {
      userInfo: userProfile,
      projectType: selectedProjectType,
      masterProjectName,
    };

    const masterResponse = await UseAxios(fetchAddProject(masterPayload, { isMaster: true }));
    const masterResponseData = parseResponseData(masterResponse.data);
    const savedMasterProject = getFirstValueByPaths(masterResponseData, [
      'masterProject',
      'data.masterProject',
      'data',
    ]);
    const savedMasterProjectId = getFirstValueByPaths(savedMasterProject, [
      'masterProjectId',
      'master_project_id',
      'id',
      'value',
    ]);
    const savedMasterProjectName = getFirstValueByPaths(savedMasterProject, [
      'masterProjectName',
      'master_project_name',
      'name',
      'label',
    ]) || masterProjectName;
    const isMasterCreated = masterResponseData.success
      || masterResponseData.status === 201
      || Boolean(savedMasterProjectName && savedMasterProject);

    if (isMasterCreated) {
      snackbarOpen(masterResponseData.message || 'Master project created successfully', 'success');
      setMasterData((prev) => ({
        ...prev,
        masterProjectName: dedupeOptions([
          ...(prev.masterProjectName || []),
          {
            id: String(savedMasterProjectId || savedMasterProjectName),
            name: String(savedMasterProjectName),
          },
        ]),
      }));
      setFormData({});
      setErrors({});
      return;
    }

    snackbarOpen(masterResponseData.message || 'Failed to create master project', 'error');
  }, [formData.masterProjectName, selectedProjectType, snackbarOpen, userProfile]);

  const syncProjectTechnologyMapping = useCallback(async (projectId) => {
    const resolvedProjectId = toInteger(projectId);
    const resolvedTechId = resolveIntegerId(formData.technology, masterData?.technology);

    if (!resolvedProjectId || !resolvedTechId) {
      return;
    }

    const projectTechnologyPayload = {
      projectId: resolvedProjectId,
      techId: resolvedTechId,
    };

    const syncResponse = await UseAxios(createProjectTechnology(projectTechnologyPayload));
    const syncData = parseResponseData(syncResponse?.data);

    if (syncData?.name === 'AxiosError' || syncData?.isAxiosError) {
      throw new Error(getServerMessage(syncData, 'Failed to save project technology mapping'));
    }
  }, [formData.technology, masterData?.technology]);

  const handleProjectSaveResponse = useCallback((data) => {
    if (data.success) {
      snackbarOpen(isEditMode ? 'Project updated successfully' : 'Project created successfully', 'success');

      // Extract newly created Project ID / Seq ID from response
      const resolvedProjectId = getFirstValueByPaths(data, [
        'data.project_id',
        'data.projectId',
        'data.project.project_id',
      ]) || projectIdFromQuery;

      const resolvedProjectSeqId = getFirstValueByPaths(data, [
        'data.project_seq_id',
        'data.projectSeqId',
        'data.project.project_seq_id',
      ]) || projectSeqIdFromQuery;

      // Navigate to Project Details page on BOTH Creation and Update
      if (resolvedProjectId) {
        const projectDetailsQuery = new URLSearchParams({
          projectId: String(resolvedProjectId),
        });
        if (resolvedProjectSeqId) {
          projectDetailsQuery.set('projectSeqId', String(resolvedProjectSeqId));
        }
        navigate(`/delivery/project-details?${projectDetailsQuery.toString()}`);
        return;
      }

      // Fallback if no ID is present
      navigate('/delivery/project-overview');
      return;
    }

    snackbarOpen(
      data.message || (isEditMode ? 'Failed to update project' : 'Failed to create project'),
      'error',
    );
  }, [isEditMode, navigate, projectIdFromQuery, projectSeqIdFromQuery, snackbarOpen]);

  /**
   * Handle save button click
   */
  const handleSave = async () => {
    if (!canSaveProjectComputed) {
      snackbarOpen(
        isEditMode ? 'You do not have access to edit projects' : 'You do not have access to add projects',
        'error',
      );
      return;
    }

    if (!handleValidation()) return;

    try {
      setIsLoading(true);

      if (currentProjectType === PROJECT_TYPES.MASTER) {
        await handleMasterSave();
        return;
      }

      const payload = buildProjectDetailsPayload({
        formData,
        currentProjectType,
        masterData,
        countryIdByName: countryIdByNameRef.current,
      });

      if (isEditMode) {
        payload.userInfo = userProfile;
      }

      if (isEditMode && projectIdFromQuery) {
        payload.project_id = toInteger(projectIdFromQuery) ?? projectIdFromQuery;
      }

      debugLog('[AddProject] Final submit payload', payload);

      const response = isEditMode && projectIdFromQuery
        ? await UseAxios(updateAddProject(projectSeqIdFromQuery || projectIdFromQuery, payload))
        : await UseAxios(fetchAddProject(payload));
      const data = parseResponseData(response.data);

      if (data?.success) {
        const savedProjectId = getFirstValueByPaths(data, [
          'data.project_id',
          'data.projectId',
          'data.project.project_id',
          'data.project.projectId',
        ]) || payload.project_id || projectIdFromQuery;

        try {
          await syncProjectTechnologyMapping(savedProjectId);
        } catch (syncError) {
          console.error('Project saved but technology sync failed:', syncError);
          snackbarOpen(
            getServerMessage(syncError, 'Project saved, but technology mapping could not be synced'),
            'warning',
          );
        }
      }

      handleProjectSaveResponse(data);
    } catch (error) {
      console.error('Error saving project:', error);
      snackbarOpen(
        getServerMessage(error, isEditMode ? 'Error updating project' : 'Error creating project'),
        'error',
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ==================== RENDER HELPERS ====================

  /**
   * Get dropdown options for a field
   */
  const getFieldOptions = useCallback(
    (field) => {
      if (field.name === 'vgNvg') {
        return VG_NVG_OPTIONS;
      }
      if (field.name === 'effortLoggingInDarpan') {
        return EFFORT_LOGGING_OPTIONS;
      }
      if (field.name === 'deliveryApproval') {
        return DELIVERY_APPROVAL_OPTIONS;
      }
      if (field.name === 'projectDuration') {
        return EFFORT_TYPE_OPTIONS;
      }
      if (field.name === 'salesSpoc') {
        return [
          SALES_SPOC_NA_OPTION,
          ...(masterData.seniorManager || []),
        ];
      }
      if (field.dataSource) {
        if (field.dataSource === 'billingType') {
          return billingTypeOptionsSource === PROJECT_TYPES.FIXED_PRICE
            ? masterData.fpBillingType || []
            : masterData.tmBillingType || [];
        }
        return masterData[field.dataSource] || [];
      }
      return [];
    },
    [billingTypeOptionsSource, masterData],
  );

  /**
   * Check if field should be disabled
   */
  const isFieldDisabled = useCallback(
    (field) => {
      if (field.name === 'sponsorName') {
        return !isInvestmentSponsorSelected(formData.sponsors);
      }

      if (field.name === 'country') {
        const regionValue = formData.region;
        return regionValue === undefined || regionValue === null || regionValue === '';
      }

      if (!field.dependsOn) return false;

      const dependsOnValue = formData[field.dependsOn];
      return dependsOnValue === undefined || dependsOnValue === null || dependsOnValue === '';
    },
    [formData, isInvestmentSponsorSelected],
  );

  /**
   * Check if field is required
   */
  const isFieldRequired = useCallback(
    (fieldName) => {
      if (requiredFieldsForType.includes(fieldName)) return true;
      if (fieldName === 'country' && formData.region) return true;
      if (fieldName === 'sponsorName' && isInvestmentSponsorSelected(formData.sponsors)) return true;
      return false;
    },
    [requiredFieldsForType, formData, isInvestmentSponsorSelected],
  );

  const getFieldErrorText = useCallback(
    (fieldName) => {
      const errorType = errors[fieldName];
      if (!errorType) return '';
      if (errorType === 'invalidSalesGMPercent') {
        return `Sales GM% must be "-", NA, or between ${SALES_GM_MIN} and ${SALES_GM_MAX}`;
      }
      if (errorType === 'invalidNoOfResources') {
        return 'Only numeric values are allowed';
      }
      return 'This field is required';
    },
    [errors],
  );

  /**
   * Render individual input field
   */
  const renderInputField = (field) => {
    const hasError = errors[field.name];
    const isRequired = isFieldRequired(field.name);
    const isMultiSelectField = field.type === 'multiselect';
    const fieldOptions = getFieldOptions(field);
    let value = formData[field.name] || '';
    if (isMultiSelectField) {
      value = Array.isArray(formData[field.name]) ? formData[field.name] : [];
    }
    const normalizedMultiSelectValue = isMultiSelectField
      ? normalizeMultiSelectValues(value, fieldOptions)
      : [];
    const isEmptyValue = (val) => (Array.isArray(val) ? val.length === 0 : !val);
    const isEmpty = isEmptyValue(isMultiSelectField ? normalizedMultiSelectValue : value);
    const disabled = isFieldDisabled(field);
    const hasVertical = Boolean(formData.vertical);
    const emptyDropdownLabel = toDarpanPlaceholderLabel(field.label, field.name);

    const toggleOfferingOption = (optionId) => {
      const normalizedOptionId = String(optionId);
      const currentValues = Array.isArray(normalizedMultiSelectValue) ? normalizedMultiSelectValue : [];
      const nextValues = currentValues.includes(normalizedOptionId)
        ? currentValues.filter((val) => val !== normalizedOptionId)
        : [...currentValues, normalizedOptionId];
      handleInputChange(field.name, nextValues);
    };

    return (
      <div key={field.name} className="input-field-container">
        {field.type !== 'checkbox' && (
          <label className="input-label">
            {field.label}
            {isRequired && <span className="required-star"> *</span>}
          </label>
        )}

        {field.type === 'checkbox' && (
          <div className="field-wrapper">
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={Boolean(formData[field.name])}
                onChange={(e) => handleInputChange(field.name, e.target.checked)}
                disabled={disabled}
              />
              <span>
                {field.label}
                {isRequired && <span className="required-star"> *</span>}
              </span>
            </label>
          </div>
        )}

        {field.name === 'dlGroupId' && (
          <div className="field-wrapper dl-group-field">
            <input
              type="text"
              className={`input-field ${hasError ? 'error-input' : ''}`}
              placeholder="DL Group ID"
              value={value}
              readOnly
              disabled={disabled}
            />
            <button
              type="button"
              className="dl-group-add-button"
              onClick={openDlDialog}
              disabled={disabled}
              aria-label={value ? 'Edit DL Group ID' : 'Add DL Group ID'}
            >
              {value ? 'Edit' : '+'}
            </button>
          </div>
        )}

        {field.name === 'jiraProjectId' && (
          <div className="field-wrapper dl-group-field jira-project-field">
            <input
              type="text"
              className={`input-field ${hasError ? 'error-input' : ''}`}
              placeholder="JIRA Project Id"
              value={value}
              readOnly
              disabled={disabled}
            />
            <button
              type="button"
              className="dl-group-add-button"
              onClick={openJiraDialog}
              disabled={disabled}
              aria-label={value ? 'Edit JIRA Project Id' : 'Add JIRA Project Id'}
            >
              {value ? 'Edit' : '+'}
            </button>
          </div>
        )}

        {/* Text Input */}
        {field.type === 'text' && !['dlGroupId', 'jiraProjectId'].includes(field.name) && (
          <div className="field-wrapper">
            <input
              type="text"
              className={`input-field ${hasError ? 'error-input' : ''}`}
              placeholder={field.placeholder}
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              inputMode={['noOfResources', 'billingId', 'redmineId', 'hubspotId'].includes(field.name) ? 'numeric' : undefined}
              pattern={['noOfResources', 'billingId', 'redmineId', 'hubspotId'].includes(field.name) ? '[0-9]*' : undefined}
              disabled={disabled}
            />
          </div>
        )}

        {/* Date Input - MUI DatePicker via CommonDatePicker component */}
        {field.type === 'date' && (
          <div className="field-wrapper is-date-picker">
            <CommonDatePicker
              value={value ? dayjs(value, 'YYYY-MM-DD') : null}
              onChange={(newValue) => {
                if (!newValue || !dayjs(newValue).isValid()) {
                  handleInputChange(field.name, '');
                  return;
                }
                handleInputChange(field.name, dayjs(newValue).format('YYYY-MM-DD'));
              }}
              label=""
              placeholder={field.placeholder}
              isDisabled={disabled}
              error={Boolean(hasError)}
              helperText=""
              displayFormat="YYYY-MM-DD"
            />
          </div>
        )}

        {/* Cascading Multi-Select: Offering Field, driven by formData.vertical */}
        {field.type === 'multiselect' && (
          <div
            className={`field-wrapper is-select ${isEmpty ? 'is-empty' : 'has-value'} ${field.hasVerticalSelection && loadingStates.offering ? 'is-loading' : ''}`}
            data-placeholder=""
          >
            <FormControl fullWidth size="small">
              <MuiSelect
                multiple
                displayEmpty
                value={normalizedMultiSelectValue}
                open={field.name === 'offering' ? offeringDropdownOpen : undefined}
                onOpen={(event) => {
                  handleMenuOpen(field.name)(event);
                  if (field.name === 'offering') {
                    setOfferingDropdownOpen(true);
                  }
                }}
                onClose={() => {
                  if (field.name === 'offering') {
                    setOfferingDropdownOpen(false);
                  }
                }}
                IconComponent={() => null}
                MenuProps={getMenuProps(field.name)}
                onChange={(e) => {
                  const selectedValues = Array.isArray(e.target.value)
                    ? e.target.value.map(String)
                    : [String(e.target.value)];

                  if (selectedValues.includes('__done__')) {
                    if (field.name === 'offering') {
                      setOfferingDropdownOpen(false);
                    }
                    return;
                  }
                  if (selectedValues.includes('__empty__')) {
                    handleInputChange(field.name, []);
                    return;
                  }
                  handleInputChange(field.name, selectedValues.filter((val) => val !== '__done__'));
                }}
                input={<OutlinedInput className={`input-field ${hasError ? 'error-input' : ''}`} />}
                renderValue={() => {
                  if (field.hasVerticalSelection && loadingStates.offering) {
                    return <span className="loading-text">Loading offerings...</span>;
                  }
                  if (field.hasVerticalSelection) {
                    if (hasVertical) {
                      const currentValues = normalizedMultiSelectValue;
                      if (currentValues.length === 0) {
                        return <span className="dropdown-placeholder">{emptyDropdownLabel}</span>;
                      }
                      return <span>{`${currentValues.length} selected`}</span>;
                    }
                    return <span className="dropdown-placeholder">Select Vertical first</span>;
                  }

                  const currentValues = normalizedMultiSelectValue;
                  if (currentValues.length === 0) {
                    return <span className="dropdown-placeholder">{emptyDropdownLabel}</span>;
                  }
                  return <span>{`${currentValues.length} selected`}</span>;
                }}
                disabled={field.hasVerticalSelection ? loadingStates.offering || !hasVertical : disabled}
              >
                {field.hasVerticalSelection ? (
                  hasVertical ? (
                    <>
                      <MenuItem
                        value="__empty__"
                        onClick={() => handleInputChange(field.name, [])}
                      >
                        <ListItemText primary={emptyDropdownLabel} />
                      </MenuItem>
                      {fieldOptions.map((option) => (
                        <MenuItem
                          key={option.id}
                          value={String(option.id)}
                          selected={normalizedMultiSelectValue.includes(String(option.id))}
                          onClick={() => toggleOfferingOption(option.id)}
                        >
                          <Checkbox checked={normalizedMultiSelectValue.includes(String(option.id))} />
                          <ListItemText primary={option.name} />
                        </MenuItem>
                      ))}
                      <MenuItem
                        value="__done__"
                        onClick={() => {
                          setOfferingDropdownOpen(false);
                        }}
                      >
                        <ListItemText primary="Done" />
                      </MenuItem>
                    </>
                  ) : (
                    <MenuItem disabled>Select Vertical first</MenuItem>
                  )
                ) : (
                  <>
                    <MenuItem
                      value="__empty__"
                      onClick={() => handleInputChange(field.name, [])}
                    >
                      <ListItemText primary={emptyDropdownLabel} />
                    </MenuItem>
                    {fieldOptions.map((option) => (
                      <MenuItem
                        key={option.id}
                        value={String(option.id)}
                        selected={normalizedMultiSelectValue.includes(String(option.id))}
                        onClick={() => toggleOfferingOption(option.id)}
                      >
                        <Checkbox checked={normalizedMultiSelectValue.includes(String(option.id))} />
                        <ListItemText primary={option.name} />
                      </MenuItem>
                    ))}
                  </>
                )}
              </MuiSelect>
            </FormControl>
            <img src={dropdwn} alt="dropdown" className="input-icon" />
          </div>
        )}

        {/* Autocomplete Field (Technology, Master Project Name) */}
        {field.type === 'autocomplete' && (
          <div className="field-wrapper is-select">
            <FormControl fullWidth size="small">
              <Autocomplete
                disablePortal
                options={getFieldOptions(field)}
                value={findOptionByIdOrName(value, getFieldOptions(field))}
                inputValue={
                  String(autocompleteInputValues[field.name] || '').trim()
                    ? autocompleteInputValues[field.name]
                    : findOptionByIdOrName(value, getFieldOptions(field))?.name || ''
                }
                PopperProps={{
                  modifiers: [
                    { name: 'flip', enabled: true },
                    { name: 'preventOverflow', options: { padding: 8 } },
                  ],
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setAutocompleteInputValues((prev) => ({ ...prev, [field.name]: newInputValue }));
                    setAutocompleteOpen((prev) => ({ ...prev, [field.name]: true }));
                    return;
                  }
                  if (reason === 'clear') {
                    setAutocompleteInputValues((prev) => ({ ...prev, [field.name]: '' }));
                    setAutocompleteOpen((prev) => ({ ...prev, [field.name]: false }));
                    return;
                  }
                  if (reason === 'reset') {
                    setAutocompleteInputValues((prev) => ({ ...prev, [field.name]: newInputValue || '' }));
                    return;
                  }
                  const currentSelected = findOptionByIdOrName(value, getFieldOptions(field));
                  setAutocompleteInputValues((prev) => ({ ...prev, [field.name]: currentSelected?.name || newInputValue || '' }));
                }}
                onChange={(event, newValue) => {
                  handleInputChange(field.name, newValue ? String(newValue.id) : '');
                  setAutocompleteInputValues((prev) => ({ ...prev, [field.name]: newValue?.name || '' }));
                  setAutocompleteOpen((prev) => ({ ...prev, [field.name]: false }));
                }}
                onBlur={() => {
                  const resolvedSelection = resolveAutocompleteSelection(
                    field.name,
                    autocompleteInputValues[field.name],
                    getFieldOptions(field),
                  );
                  if (resolvedSelection) {
                    handleInputChange(field.name, resolvedSelection.id);
                    setAutocompleteInputValues((prev) => ({
                      ...prev,
                      [field.name]: resolvedSelection.name,
                    }));
                  }
                  setAutocompleteOpen((prev) => ({ ...prev, [field.name]: false }));
                }}
                isOptionEqualToValue={(option, selected) => String(option.id) === String(selected.id)}
                getOptionLabel={(option) => option?.name || ''}
                popupIcon={null}
                forcePopupIcon={false}
                open={
                  Boolean(autocompleteOpen[field.name])
                  && String(autocompleteInputValues[field.name] || '').trim().length >= 1
                }
                filterOptions={(options, state) => {
                  const query = state.inputValue?.toLowerCase() || '';
                  return options.filter((opt) => (opt?.name || '').toLowerCase().includes(query));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={field.placeholder}
                    InputProps={{
                      ...params.InputProps,
                      className: `input-field ${hasError ? 'error-input' : ''}`,
                      endAdornment: null,
                    }}
                  />
                )}
                componentsProps={{ paper: { className: 'add-project-menu' } }}
                disabled={disabled}
              />
            </FormControl>
          </div>
        )}

        {field.type === 'multi-autocomplete' && (
          <div className="field-wrapper is-select">
            <FormControl fullWidth size="small">
              <Autocomplete
                multiple
                disablePortal
                options={getFieldOptions(field)}
                value={(Array.isArray(value) ? value : [])
                  .map((selectedValue) => findOptionByIdOrName(selectedValue, getFieldOptions(field)))
                  .filter(Boolean)}
                inputValue={autocompleteInputValues[field.name] ?? ''}
                onChange={(event, newValue) => {
                  handleInputChange(
                    field.name,
                    Array.isArray(newValue) ? newValue.map((item) => String(item.id)) : [],
                  );
                  setAutocompleteInputValues((prev) => ({ ...prev, [field.name]: '' }));
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setAutocompleteInputValues((prev) => ({ ...prev, [field.name]: newInputValue || '' }));
                  }
                }}
                isOptionEqualToValue={(option, selected) => String(option.id) === String(selected.id)}
                getOptionLabel={(option) => option?.name || ''}
                filterSelectedOptions
                popupIcon={null}
                forcePopupIcon={false}
                filterOptions={(options, state) => {
                  const query = String(state.inputValue || '').trim()
                    .toLowerCase();
                  if (!query) return options;

                  return options
                    .filter((opt) => (opt?.name || '').toLowerCase().includes(query));
                }}
                renderTags={(tagValue, getTagProps) =>
                  (() => {
                    const isExpanded = Boolean(expandedMultiAutocomplete[field.name]);
                    const visibleTags = isExpanded ? tagValue : tagValue.slice(0, 1);
                    const hiddenCount = isExpanded ? 0 : Math.max(tagValue.length - visibleTags.length, 0);

                    return [
                      ...visibleTags.map((option, index) => {
                        const { key, onDelete } = getTagProps({ index });
                        return (
                          <span key={key} className="multi-autocomplete-tag">
                            <span className="multi-autocomplete-tag__label">{option.name}</span>
                            <button
                              type="button"
                              className="multi-autocomplete-tag__remove"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onDelete?.(event);
                              }}
                              aria-label={`Remove ${option.name}`}
                            >
                              x
                            </button>
                          </span>
                        );
                      }),
                      hiddenCount > 0 && (
                        <button
                          key={`${field.name}-summary`}
                          type="button"
                          className="multi-autocomplete-summary"
                          onClick={() =>
                            setExpandedMultiAutocomplete((prev) => ({ ...prev, [field.name]: true }))
                          }
                        >
                          +{hiddenCount}
                        </button>
                      ),
                      isExpanded && tagValue.length > 1 && (
                        <button
                          key={`${field.name}-collapse`}
                          type="button"
                          className="multi-autocomplete-summary"
                          onClick={() =>
                            setExpandedMultiAutocomplete((prev) => ({ ...prev, [field.name]: false }))
                          }
                        >
                          less
                        </button>
                      ),
                    ].filter(Boolean);
                  })()
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={field.placeholder}
                    InputProps={{
                      ...params.InputProps,
                      className: `input-field multi-autocomplete-input ${hasError ? 'error-input' : ''}`,
                    }}
                  />
                )}
                componentsProps={{ paper: { className: 'add-project-menu' } }}
                disabled={disabled}
              />
            </FormControl>
          </div>
        )}

        {/* Regular Select Dropdown - with SearchDropdown for manager fields */}
        {field.type === 'select' && MANAGER_FIELDS.has(field.name) && (
          <SearchDropdown
            value={findOptionByIdOrName(value, getFieldOptions(field))}
            dropdownCss="manager-search-dropdown"
            searchBox={true}
            placeholder={emptyDropdownLabel}
            options={getFieldOptions(field)}
            emptyMenuText={emptyDropdownLabel}
            onChange={(e) =>
              handleInputChange(
                field.name,
                e?.target?.value?.id
                || e?.target?.value?.value
                || e?.value?.id
                || e?.value
                || e?.id
                || '',
              )
            }
            menuProps={getMenuProps(field.name)}
          />
        )}

        {/* Regular Select Dropdown - MUI version for other fields */}
        {field.type === 'select' && !MANAGER_FIELDS.has(field.name) && (
          <div
            className={`field-wrapper is-select ${field.name === 'deliveryApproval' ? 'delivery-approval-select' : ''} ${isEmpty ? 'is-empty' : 'has-value'}`}
            data-placeholder=""
          >
            <FormControl fullWidth size="small">
              <MuiSelect
                displayEmpty
                value={value}
                onChange={(e) => handleInputChange(field.name, e.target.value)}
                onOpen={handleMenuOpen(field.name)}
                input={<OutlinedInput className={`input-field ${hasError ? 'error-input' : ''}`} />}
                renderValue={(__selected) => {
                  if (!__selected) {
                    return <span className="dropdown-placeholder">{emptyDropdownLabel}</span>;
                  }
                  const match = getFieldOptions(field).find((opt) => String(opt.id) === String(__selected));
                  return match ? match.name : __selected;
                }}
                disabled={disabled}
                IconComponent={() => null}
                MenuProps={getMenuProps(field.name)}
              >
                <MenuItem value="">{emptyDropdownLabel}</MenuItem>
                {getFieldOptions(field).map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </MuiSelect>
            </FormControl>
            <img src={dropdwn} alt="dropdown" className="input-icon" />
          </div>
        )}

        {hasError && <div className="error-text">{getFieldErrorText(field.name)}</div>}
      </div>
    );
  };

  // ==================== MAIN RENDER ====================

  const projectTypeFromBillingType = useMemo(() => {
    const billingTypeValue = formData?.billingType;
    if (billingTypeValue === undefined || billingTypeValue === null || String(billingTypeValue).trim() === '') {
      return '';
    }

    const tmMatch = findOptionByIdOrName(billingTypeValue, masterData?.tmBillingType || []);
    const fpMatch = findOptionByIdOrName(billingTypeValue, masterData?.fpBillingType || []);

    if (tmMatch && !fpMatch) return PROJECT_TYPES.TIME_AND_MATERIAL;
    if (fpMatch && !tmMatch) return PROJECT_TYPES.FIXED_PRICE;

    const billingTypeText = String(tmMatch?.name || fpMatch?.name || billingTypeValue).trim()
      .toLowerCase();
    if (billingTypeText.includes('t&m') || billingTypeText.includes('tm')) return PROJECT_TYPES.TIME_AND_MATERIAL;
    if (billingTypeText.includes('fp') || billingTypeText.includes('fixed')) return PROJECT_TYPES.FIXED_PRICE;

    return '';
  }, [formData?.billingType, masterData?.fpBillingType, masterData?.tmBillingType]);

  const pageTitle = isEditMode ? 'Edit Projects' : ADD_PROJECT_TITLE;
  const displayProjectType = isEditMode
    ? (lockedProjectType || currentProjectType || projectTypeFromBillingType)
    : currentProjectType;
  const editProjectTypeLabel = displayProjectType === PROJECT_TYPES.FIXED_PRICE
    ? 'FP project'
    : displayProjectType === PROJECT_TYPES.TIME_AND_MATERIAL
      ? 'TM project'
      : 'Master project';

  return (
    <div className="add-project-container">
      <div className="header-section">
        <div className="title-with-back">
          {isEditMode && (
            <button
              type="button"
              className="back-button"
              onClick={handleBackNavigation}
              aria-label="Back to Project Overview"
            >
              <img src={backIcon} alt="back" />
            </button>
          )}
          <h1 className="add-project-title">{pageTitle}</h1>
        </div>

        <div className="dropdown-container">
          {isEditMode ? (
            <div className="project-type-readonly">{editProjectTypeLabel}</div>
          ) : (
            <SearchDropdown
              value={selectedProjectType}
              dropdownCss="skill-search-dropdown"
              searchBox={false}
              placeholder={SELECT}
              options={PROJECT_TYPE_OPTIONS}
              onChange={handleProjectTypeChange}
              menuProps={getMenuProps('projectType')}
              disabled={isEditMode}
            />
          )}

          {shouldShowInternalProjectCheckbox && (
            <div className="internal-project-inline">
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={Boolean(formData.internalProject)}
                  onChange={(e) => handleInputChange('internalProject', e.target.checked)}
                />
                <span>Intenal Project</span>
              </label>
            </div>
          )}
        </div>
      </div>

      {isPrefillLoading ? (
        <div className="add-project-loader-wrapper">
          <Loader />
        </div>
      ) : (
        <>
          {prefillError && (
            <div className="prefill-status-message">{prefillError}</div>
          )}

          <div className="form-container form-grid">{visibleFields.map(renderInputField)}</div>

          <div className="save-button-container">
            <ButtonComponent
              btnCss="set-save-btn"
              label={SAVE}
              variant="contained"
              onClick={handleSave}
              disabled={isLoading || isPrefillLoading || loadingStates.masterData || !canSaveProjectComputed}
            />
          </div>
        </>
      )}

      {dlDialogState.open && (
        <div className="dl-dialog-backdrop" role="presentation">
          <div className="dl-dialog" role="dialog" aria-modal="true" aria-labelledby="dl-dialog-title">
            <div className="dl-dialog-header">
              <h2 id="dl-dialog-title">Add DL Id</h2>
              <button type="button" className="dl-dialog-close" onClick={closeDlDialog} aria-label="Close DL Id dialog">
                x
              </button>
            </div>

            <input
              type="text"
              className="input-field dl-dialog-input"
              placeholder="DL Id"
              value={dlDialogState.inputValue}
              onChange={handleDlDialogInputChange}
              disabled={dlDialogState.isValidating}
              autoFocus
            />

            {dlDialogState.errorMessage && (
              <div className="dl-dialog-error">{dlDialogState.errorMessage}</div>
            )}
            {dlDialogState.duplicateMessage && (
              <div className="dl-dialog-error">{dlDialogState.duplicateMessage}</div>
            )}

            <div className="dl-dialog-actions">
              {dlDialogState.duplicateMessage && (
                <button
                  type="button"
                  className="dl-dialog-button"
                  onClick={confirmDuplicateDlGroupId}
                  disabled={dlDialogState.isValidating}
                >
                  Yes
                </button>
              )}
              <button
                type="button"
                className="dl-dialog-button"
                onClick={handleDlValidation}
                disabled={dlDialogState.isValidating}
              >
                {dlDialogState.isValidating ? 'Validating...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {jiraDialogState.open && (
        <div className="dl-dialog-backdrop" role="presentation">
          <div className="dl-dialog" role="dialog" aria-modal="true" aria-labelledby="jira-dialog-title">
            <div className="dl-dialog-header">
              <h2 id="jira-dialog-title">Add Jira Project Id</h2>
              <button type="button" className="dl-dialog-close" onClick={closeJiraDialog} aria-label="Close Jira Project Id dialog">
                x
              </button>
            </div>

            <input
              type="text"
              className="input-field dl-dialog-input"
              placeholder="JIRA Project Id"
              value={jiraDialogState.inputValue}
              onChange={handleJiraDialogInputChange}
              disabled={jiraDialogState.isValidating}
              autoFocus
            />

            {jiraDialogState.errorMessage && (
              <div className="dl-dialog-error">{jiraDialogState.errorMessage}</div>
            )}

            <div className="dl-dialog-actions">
              <button
                type="button"
                className="dl-dialog-button"
                onClick={handleJiraValidation}
                disabled={jiraDialogState.isValidating}
              >
                {jiraDialogState.isValidating ? 'Validating...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddProject;
